import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { games, FLAGS, buildFlagSet, quizQuestions, LETTERS, computeScores, MINI_GAMES } from './games.js';
import { BeerModal } from './Beer.jsx';
import { ChallengeModal, ChallengeInbox, MiniGamesPanel } from './MiniGames.jsx';
import { ChatModal } from './Chat.jsx';
import { DrawingBoard, WhiteboardMini } from './Drawing.jsx';

// ============================================================
//                       DESIGN TOKENS
// ============================================================
const C = {
  bg: '#0a0a0a', card: '#161616', cardHi: '#1f1f1f',
  border: '#2a2a2a', borderHi: '#3a3a3a',
  text: '#fafafa', muted: '#a1a1aa', dim: '#71717a',
  alex: '#ef4444', alexDim: '#7f1d1d',
  group: '#f5c518', groupDim: '#854d0e',
  win: '#22c55e',
  multi: '#8b5cf6', live: '#0ea5e9',
};

const MODE_BADGE = {
  multi: { icon: '📱', label: 'Multi · Smartphone', color: C.multi },
  live:  { icon: '🌍', label: 'Live · vor Ort', color: C.live },
};

// ============================================================
//          CONNECTION HOOK (WebSocket-based shared state)
// ============================================================
function useConnection() {
  const [shared, setShared] = useState({
    history: [], activity: null, customGames: [],
    votes: {}, estimates: {}, flagAnswers: {}, alexAnswers: {},
    alexQuiz: { pool: [] }, rouletteHist: [],
    drawing: { strokes: [], mode: null },
    manualParticipants: [],
    chats: [], miniGames: {}, challenges: [],
  });
  const [presence, setPresence] = useState([]);
  const [me, setMe] = useState(null);
  const [status, setStatus] = useState('connecting');
  const [strokes, setStrokes] = useState([]);
  const wsRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('t');
    if (!token) { setStatus('no-token'); return; }

    // Stable client id in localStorage so reconnects keep same userId + name on server
    let cid = localStorage.getItem('sda-cid-v1');
    if (!cid) {
      cid = Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-6);
      localStorage.setItem('sda-cid-v1', cid);
    }

    let stopped = false;
    let everOpened = false;
    let retry = 0;
    let timer = null;

    function connect() {
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const storedName = localStorage.getItem('sda-name-v1') || '';
      const qs = `t=${encodeURIComponent(token)}&cid=${encodeURIComponent(cid)}` + (storedName ? `&n=${encodeURIComponent(storedName)}` : '');
      const ws = new WebSocket(`${proto}//${location.host}/ws?${qs}`);
      wsRef.current = ws;
      ws.onopen = () => { everOpened = true; retry = 0; setStatus('open'); };
      ws.onmessage = e => {
        let msg; try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.type === 'hello') setMe({ role: msg.role, userId: msg.userId, name: msg.name });
        else if (msg.type === 'state') {
          setShared(s => ({ ...s, ...msg.state }));
          setStrokes(msg.state?.drawing?.strokes || []);
          if (msg.presence) setPresence(msg.presence);
        }
        else if (msg.type === 'presence') setPresence(msg.presence || []);
        else if (msg.type === 'draw-start') {
          setStrokes(prev => [...prev, msg.stroke]);
        }
        else if (msg.type === 'draw-extend') {
          setStrokes(prev => prev.map(s => s.id === msg.strokeId ? { ...s, points: [...s.points, ...msg.points] } : s));
        }
        else if (msg.type === 'draw-end') {
          setStrokes(prev => prev.map(s => s.id === msg.strokeId ? { ...s, done: true } : s));
        }
        else if (msg.type === 'draw-clear') {
          setStrokes([]);
        }
      };
      ws.onerror = () => {};
      ws.onclose = (e) => {
        if (stopped) return;
        // 4001 = leader kicked this client. Stop reconnecting; show terminal state.
        if (e && e.code === 4001) { stopped = true; setStatus('kicked'); return; }
        if (!everOpened) { setStatus('denied'); return; }
        setStatus('reconnecting');
        retry = Math.min(retry + 1, 6);
        timer = setTimeout(connect, Math.min(500 * 2 ** retry, 8000));
      };
    }
    connect();
    return () => { stopped = true; if (timer) clearTimeout(timer); wsRef.current?.close(); };
  }, []);

  // Visibility tracking → broadcast active flag
  useEffect(() => {
    if (status !== 'open') return;
    const onVis = () => send({ type: 'visibility', active: !document.hidden });
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    window.addEventListener('blur', onVis);
    onVis();
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
      window.removeEventListener('blur', onVis);
    };
  }, [status]);

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(msg));
  }, []);

  const updateShared = useCallback((updater) => {
    setShared(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const ws = wsRef.current;
      if (ws && ws.readyState === 1) {
        ws.send(JSON.stringify({
          type: 'state-replace',
          state: {
            history: next.history,
            activity: next.activity,
            customGames: next.customGames,
          },
        }));
      }
      return next;
    });
  }, []);

  const setName = useCallback((name) => {
    send({ type: 'set-name', name });
    setMe(m => m ? { ...m, name } : m);
  }, [send]);

  return { shared, me, status, presence, strokes, send, updateShared, setName };
}

// ============================================================
//                          UI ATOMS
// ============================================================
const Btn = ({ children, onClick, color = 'neutral', size = 'md', className = '', style = {}, disabled = false, type = 'button' }) => {
  const base = 'font-bold rounded-xl transition-all active:scale-[0.97] disabled:opacity-30 disabled:active:scale-100';
  const sizes = { sm: 'px-3 py-2 text-xs', md: 'px-4 py-3 text-sm', lg: 'px-6 py-4 text-base', xl: 'px-8 py-5 text-lg' };
  const colors = {
    alex: { background: C.alex, color: '#fff' },
    group: { background: C.group, color: '#000' },
    neutral: { background: C.cardHi, color: C.text, border: `1px solid ${C.border}` },
    ghost: { background: 'transparent', color: C.muted, border: `1px solid ${C.border}` },
    win: { background: C.win, color: '#000' },
    multi: { background: C.multi, color: '#fff' },
  };
  return <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${sizes[size]} ${className}`}
    style={{ ...colors[color], ...style }}>{children}</button>;
};

const Card = ({ children, className = '', style = {}, onClick }) => (
  <div onClick={onClick} style={{ background: C.card, border: `1px solid ${C.border}`, ...style }}
    className={`rounded-2xl ${onClick ? 'cursor-pointer hover:border-zinc-700 transition' : ''} ${className}`}>
    {children}
  </div>
);

const Display = ({ children, className = '', style = {} }) => (
  <span style={{ fontFamily: 'Anton, Impact, sans-serif', letterSpacing: '0.02em', ...style }} className={className}>{children}</span>
);

const Scoreboard = ({ scores, big = false }) => (
  <Card className={big ? 'p-6' : 'p-4'}>
    <div className="grid grid-cols-3 items-center">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ color: C.alex }}>Alex</div>
        <Display className={big ? 'text-7xl leading-none' : 'text-4xl leading-none'} style={{ color: C.alex }}>{scores.alex}</Display>
      </div>
      <div className="text-center font-black opacity-30" style={{ fontSize: big ? '1rem' : '0.7rem' }}>VS</div>
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ color: C.group }}>Gruppe</div>
        <Display className={big ? 'text-7xl leading-none' : 'text-4xl leading-none'} style={{ color: C.group }}>{scores.challenger}</Display>
      </div>
    </div>
  </Card>
);

const ModeBadge = ({ mode }) => {
  const m = MODE_BADGE[mode] || MODE_BADGE.live;
  return (
    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1"
      style={{ background: 'rgba(255,255,255,0.04)', color: m.color, border: `1px solid ${m.color}33` }}>
      <span>{m.icon}</span> {mode === 'multi' ? 'MULTI' : 'LIVE'}
    </span>
  );
};

const StatusBanner = ({ status }) => {
  if (status === 'open') return null;
  const labels = {
    connecting: 'Verbinde…',
    reconnecting: 'Verbindung verloren – versuche erneut…',
    denied: 'Token ungültig oder abgelaufen.',
    'no-token': 'Kein Zugangstoken in der URL.',
    kicked: 'Vom Spielleiter entfernt.',
  };
  const colors = { denied: C.alex, 'no-token': C.alex, kicked: C.alex, reconnecting: C.group, connecting: C.muted };
  return (
    <div className="fixed top-0 left-0 right-0 z-50 text-center py-2 text-xs uppercase tracking-widest"
      style={{ background: 'rgba(10,10,10,0.95)', color: colors[status] || C.muted, borderBottom: `1px solid ${C.border}` }}>
      {labels[status] || status}
    </div>
  );
};

// ============================================================
//                       HEADER
// ============================================================
const Header = ({ scores, me, onHome, view, onlineCount, onShowOnline, onChat, chatUnread, onChallenge, onWhiteboard, whiteboardActive }) => {
  const accent = me.role === 'leader' ? C.alex : me.role === 'alex' ? C.alex : C.group;
  return (
    <div className="sticky top-0 z-20" style={{ background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(10px)', borderBottom: `1px solid ${C.border}` }}>
      <div className="px-3 py-2 flex items-center gap-2">
        <button onClick={onHome} className="flex items-center gap-1 text-xs uppercase tracking-[0.2em] font-bold opacity-70 hover:opacity-100 transition" style={{ color: C.muted }}>
          {view !== 'home' ? '←' : '☰'} {view === 'home' ? 'SDA' : 'Home'}
        </button>
        <div className="flex-1 min-w-0 text-center">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest truncate" style={{ color: accent }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
            <span className="font-bold truncate">{me.name}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Display className="text-lg leading-none" style={{ color: C.alex }}>{scores.alex}</Display>
          <span className="font-black text-[10px] opacity-30">:</span>
          <Display className="text-lg leading-none" style={{ color: C.group }}>{scores.challenger}</Display>
        </div>
      </div>
      <div className="px-3 pb-2 flex items-center gap-1.5">
        <HeaderBtn onClick={onShowOnline} label={`● ${onlineCount}`} color={C.win} title="Online" />
        <HeaderBtn onClick={onChat} label={`💬${chatUnread ? ` ${chatUnread}` : ''}`} color={chatUnread ? C.win : C.muted} title="Chat" />
        <HeaderBtn onClick={onChallenge} label="🎮" color={C.muted} title="Mini-Spiel" />
        <HeaderBtn onClick={onWhiteboard} label={whiteboardActive ? '🎨 ●' : '🎨'} color={whiteboardActive ? C.win : C.muted} title="Whiteboard" />
      </div>
    </div>
  );
};

const HeaderBtn = ({ onClick, label, color, title }) => (
  <button onClick={onClick} title={title}
    className="rounded-md px-2 py-1 text-[10px] uppercase tracking-widest font-bold active:scale-95"
    style={{ background: C.cardHi, color, border: `1px solid ${C.border}` }}>
    {label}
  </button>
);

// ============================================================
//          MODAL & BOTTOM-SHEET
// ============================================================
const Modal = ({ open, onClose, children, title }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div className="w-full sm:max-w-md max-h-[88vh] overflow-auto rounded-t-2xl sm:rounded-2xl"
        style={{ background: C.card, border: `1px solid ${C.border}` }}
        onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b" style={{ background: C.card, borderColor: C.border }}>
          <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.muted }}>{title}</div>
          <button onClick={onClose} className="text-lg leading-none" style={{ color: C.dim }}>×</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

// ============================================================
//          PRESENCE PANEL
// ============================================================
const PresencePanel = ({ open, onClose, presence, me, manualParticipants, send }) => {
  const players = presence.filter(p => p.role === 'player');
  const alex = presence.find(p => p.role === 'alex');
  const leader = presence.find(p => p.role === 'leader');
  const activePlayers = players.filter(p => p.active);
  const manual = manualParticipants || [];
  const [newName, setNewName] = useState('');

  const addManual = () => {
    const n = newName.trim();
    if (!n) return;
    send({ type: 'manual-tn-add', name: n });
    setNewName('');
  };

  return (
    <Modal open={open} onClose={onClose} title={`Teilnehmer · ${presence.length + manual.length}`}>
      <div className="space-y-3">
        {leader && (
          <Card className="p-3 flex items-center gap-3" style={{ borderColor: C.alex }}>
            <span className="w-2 h-2 rounded-full" style={{ background: leader.active ? C.win : C.dim }} />
            <div className="flex-1">
              <div className="font-bold">{leader.name}</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: C.alex }}>Spielleiter</div>
            </div>
          </Card>
        )}
        {alex && (
          <Card className="p-3 flex items-center gap-3" style={{ borderColor: C.alex }}>
            <span className="w-2 h-2 rounded-full" style={{ background: alex.active ? C.win : C.dim }} />
            <div className="flex-1">
              <div className="font-bold">{alex.name}</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: C.alex }}>Alex</div>
            </div>
          </Card>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-widest mb-2 flex items-center justify-between" style={{ color: C.dim }}>
            <span>Mitspieler online · {players.length} ({activePlayers.length} aktiv)</span>
          </div>
          {players.length === 0 ? (
            <div className="text-sm" style={{ color: C.muted }}>Noch keine Mitspieler verbunden.</div>
          ) : (
            <div className="space-y-1.5">
              {players.map(p => (
                <div key={p.userId} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: C.cardHi }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: p.active ? C.win : C.dim }} />
                  <div className="flex-1 font-bold text-sm">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: p.active ? C.win : C.dim }}>
                    {p.active ? 'aktiv' : 'idle'}
                  </div>
                  {me.role === 'leader' && (
                    <button onClick={() => {
                      if (confirm(`„${p.name}" wirklich entfernen? Die Person kann sich nicht erneut verbinden, bis der Server neu gestartet wird.`)) {
                        send({ type: 'kick-player', userId: p.userId });
                      }
                    }}
                      title="Teilnehmer entfernen"
                      className="w-6 h-6 rounded-md flex items-center justify-center text-lg active:scale-90"
                      style={{ color: C.dim, background: C.card }}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {manual.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>
              Ohne Smartphone · {manual.length}
            </div>
            <div className="space-y-1.5">
              {manual.map(p => (
                <div key={p.id} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: C.cardHi }}>
                  <span className="text-base">👤</span>
                  <div className="flex-1 font-bold text-sm">{p.name}</div>
                  {me.role === 'leader' && (
                    <button onClick={() => send({ type: 'manual-tn-remove', id: p.id })}
                      className="w-6 h-6 rounded-md flex items-center justify-center text-lg active:scale-90"
                      style={{ color: C.dim, background: C.card }}>×</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {me.role === 'leader' && (
          <Card className="p-3" style={{ borderColor: C.border }}>
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>TN ohne Smartphone hinzufügen</div>
            <div className="flex gap-2">
              <input value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addManual(); }}
                placeholder="Vorname" maxLength={32}
                className="flex-1 rounded-lg px-3 py-2 text-sm"
                style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
              <Btn color="group" size="md" onClick={addManual} disabled={!newName.trim()}>Hinzufügen</Btn>
            </div>
          </Card>
        )}

        {me.role === 'leader' && (
          <div>
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Namen würfeln aus…</div>
            <div className="grid grid-cols-2 gap-2">
              <Btn color="group" size="md" onClick={() => { onClose?.(); send({ type: 'roll-name', mode: 'online' }); }}
                disabled={players.length === 0}>
                🎲 Online ({players.length})
              </Btn>
              <Btn color="neutral" size="md" onClick={() => { onClose?.(); send({ type: 'roll-name', mode: 'all' }); }}
                disabled={players.length + manual.length === 0}>
                🎲 Alle ({players.length + manual.length})
              </Btn>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// ============================================================
//          NAME ROULETTE — overlay when activity == name-roulette
// ============================================================
const NameRouletteOverlay = ({ activity, me, isLeader, onDismiss }) => {
  const [animPick, setAnimPick] = useState(activity.pickedName);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDone(false);
    let cancelled = false;
    const startTs = Date.now();
    const duration = 1500;
    const tick = () => {
      const elapsed = Date.now() - startTs;
      if (elapsed >= duration || cancelled) { setAnimPick(activity.pickedName); setDone(true); return; }
      // Random letters cycle
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const fake = Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
      setAnimPick(fake);
      setTimeout(tick, 50 + (elapsed / duration) * 200);
    };
    tick();
    return () => { cancelled = true; };
  }, [activity.id]);

  const isMe = activity.pickedUserId === me.userId;
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="text-center p-6 max-w-sm w-full">
        <div className="text-7xl mb-4">🎲</div>
        <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: C.muted }}>Name gewürfelt</div>
        <Display className={`block leading-none mb-4 ${done ? 'text-6xl' : 'text-5xl'}`}
          style={{ color: done ? (isMe ? C.alex : C.group) : C.muted }}>
          {animPick}
        </Display>
        {done && isMe && (
          <div className="mb-4 text-sm font-bold" style={{ color: C.alex }}>
            DAS BIST DU.
          </div>
        )}
        {done && isLeader && (
          <Btn color="ghost" size="md" onClick={onDismiss} className="w-full">Schließen</Btn>
        )}
        {done && !isLeader && (
          <div className="text-xs" style={{ color: C.dim }}>Warte auf den Spielleiter…</div>
        )}
      </div>
    </div>
  );
};

// ============================================================
//                       GAME TOOLS (1-on-1)
// ============================================================

const MinuteTool = () => {
  const [running, setRunning] = useState(false);
  const [startMs, setStartMs] = useState(null);
  const [alexTime, setAlexTime] = useState(null);
  const [grpTime, setGrpTime] = useState(null);
  const start = () => { setStartMs(Date.now()); setRunning(true); setAlexTime(null); setGrpTime(null); };
  const reset = () => { setRunning(false); setStartMs(null); setAlexTime(null); setGrpTime(null); };
  const stop = (who) => {
    if (!running) return;
    const t = (Date.now() - startMs) / 1000;
    if (who === 'alex' && alexTime === null) setAlexTime(t);
    if (who === 'grp' && grpTime === null) setGrpTime(t);
  };
  const diffAlex = alexTime !== null ? Math.abs(alexTime - 60) : null;
  const diffGrp = grpTime !== null ? Math.abs(grpTime - 60) : null;
  const bothDone = alexTime !== null && grpTime !== null;
  const winner = bothDone ? (diffAlex < diffGrp ? 'alex' : diffGrp < diffAlex ? 'grp' : 'tie') : null;
  return (
    <div className="space-y-4">
      <Card className="p-6 text-center">
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>
          {bothDone ? 'Ergebnis' : running ? 'Läuft – nicht auf den Bildschirm schauen!' : 'Bereit'}
        </div>
        <Display className="text-6xl" style={{ color: bothDone ? C.text : C.dim }}>{bothDone ? '60.0' : '??.?'} s</Display>
      </Card>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => stop('alex')} disabled={!running || alexTime !== null}
          className="rounded-2xl p-6 font-bold disabled:opacity-60 active:scale-95 transition"
          style={{ background: alexTime !== null ? C.alexDim : C.alex, color: '#fff' }}>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Alex Stop</div>
          {alexTime !== null && <Display className="text-3xl mt-1">{alexTime.toFixed(1)}s</Display>}
          {alexTime !== null && <div className="text-xs mt-1 opacity-80">Δ {diffAlex.toFixed(1)}s</div>}
        </button>
        <button onClick={() => stop('grp')} disabled={!running || grpTime !== null}
          className="rounded-2xl p-6 font-bold disabled:opacity-60 active:scale-95 transition"
          style={{ background: grpTime !== null ? C.groupDim : C.group, color: '#000' }}>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Herausforderer Stop</div>
          {grpTime !== null && <Display className="text-3xl mt-1">{grpTime.toFixed(1)}s</Display>}
          {grpTime !== null && <div className="text-xs mt-1 opacity-80">Δ {diffGrp.toFixed(1)}s</div>}
        </button>
      </div>
      {bothDone && (
        <Card className="p-4 text-center" style={{ borderColor: winner === 'alex' ? C.alex : winner === 'grp' ? C.group : C.border }}>
          <div className="text-xs uppercase tracking-widest" style={{ color: C.muted }}>Sieger</div>
          <Display className="text-2xl mt-1" style={{ color: winner === 'alex' ? C.alex : winner === 'grp' ? C.group : C.text }}>
            {winner === 'alex' ? 'ALEX' : winner === 'grp' ? 'HERAUSFORDERER' : 'UNENTSCHIEDEN'}
          </Display>
        </Card>
      )}
      <div className="flex gap-2">
        {!running && !bothDone && <Btn color="neutral" size="lg" onClick={start} className="flex-1">Start</Btn>}
        {(running || bothDone) && <Btn color="ghost" size="lg" onClick={reset} className="flex-1">Reset</Btn>}
      </div>
    </div>
  );
};

const DiceTool = () => {
  const [d1, setD1] = useState(null);
  const [d2, setD2] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const roll = () => { setD1(1 + Math.floor(Math.random() * 6)); setD2(1 + Math.floor(Math.random() * 6)); setRevealed(false); };
  const sorted = d1 && d2 ? [Math.max(d1, d2), Math.min(d1, d2)] : null;
  const value = sorted ? parseInt(`${sorted[0]}${sorted[1]}`) : null;
  const isMaex = sorted && sorted[0] === 2 && sorted[1] === 1;
  const isPasch = sorted && sorted[0] === sorted[1];
  const label = isMaex ? 'MÄXCHEN!' : isPasch ? `${sorted[0]}er Pasch` : value;
  return (
    <div className="space-y-4">
      <Card className="p-6 text-center">
        <div className="text-[10px] uppercase tracking-widest mb-3" style={{ color: C.dim }}>
          {d1 === null ? 'Würfeln' : revealed ? 'Aufgedeckt' : 'Tippe zum Aufdecken'}
        </div>
        <div className="flex gap-4 justify-center my-4">
          {[d1, d2].map((d, i) => (
            <div key={i} onClick={() => d !== null && setRevealed(true)}
              className="w-24 h-24 rounded-2xl flex items-center justify-center text-5xl font-black cursor-pointer transition"
              style={{ background: revealed ? C.group : C.cardHi, color: revealed ? '#000' : C.text, border: `2px solid ${revealed ? C.group : C.border}`, fontFamily: 'Anton, sans-serif' }}>
              {d === null ? '–' : revealed ? d : '?'}
            </div>
          ))}
        </div>
        {revealed && <Display className="text-3xl block mt-2" style={{ color: isMaex ? C.alex : isPasch ? C.group : C.text }}>{label}</Display>}
      </Card>
      <div className="flex gap-2">
        <Btn color="neutral" size="lg" onClick={roll} className="flex-1">Würfeln</Btn>
        {d1 !== null && !revealed && <Btn color="group" size="lg" onClick={() => setRevealed(true)} className="flex-1">Aufdecken</Btn>}
      </div>
      <Card className="p-4 text-xs" style={{ color: C.muted }}>
        <div className="font-bold mb-2" style={{ color: C.text }}>Wertung (niedrig → hoch):</div>
        <div>31 · 32 · 41 · 42 · 43 · 51 · 52 · 53 · 54 · 61 · 62 · 63 · 64 · 65</div>
        <div className="mt-1">Pasch: 11–66 (schlägt alle Nicht-Pasch)</div>
        <div className="mt-1" style={{ color: C.alex }}>Mäxchen (21) schlägt ALLES.</div>
      </Card>
    </div>
  );
};

const LetterTool = () => {
  const [letter, setLetter] = useState(null);
  const [seconds, setSeconds] = useState(90);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (!running) return;
    if (seconds <= 0) { setRunning(false); return; }
    const id = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [running, seconds]);
  const pick = () => { setLetter(LETTERS[Math.floor(Math.random() * LETTERS.length)]); setSeconds(90); setRunning(false); };
  return (
    <div className="space-y-4">
      <Card className="p-8 text-center">
        <div className="text-[10px] uppercase tracking-widest mb-3" style={{ color: C.dim }}>Buchstabe</div>
        <Display className="text-[10rem] leading-none" style={{ color: letter ? C.group : C.dim }}>{letter || '?'}</Display>
      </Card>
      <Card className="p-6 text-center">
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Verbleibend</div>
        <Display className="text-6xl" style={{ color: seconds <= 10 && running ? C.alex : C.text }}>{seconds}s</Display>
      </Card>
      <div className="grid grid-cols-2 gap-2">
        <Btn color="neutral" size="lg" onClick={pick}>{letter ? 'Neu' : 'Buchstabe'}</Btn>
        <Btn color={running ? 'ghost' : 'group'} size="lg" onClick={() => setRunning(r => !r)} disabled={!letter}>
          {running ? 'Pause' : seconds === 90 ? 'Start 90s' : 'Weiter'}
        </Btn>
      </div>
    </div>
  );
};

const MemorizeTool = () => {
  const [phase, setPhase] = useState('idle');
  const [seconds, setSeconds] = useState(30);
  useEffect(() => {
    if (phase !== 'memorize') return;
    if (seconds <= 0) { setPhase('write'); return; }
    const id = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, seconds]);
  const labels = { idle: 'Tablett vorbereiten · 20 Dinge drauflegen', memorize: 'EINPRÄGEN · jetzt anschauen!', write: 'AUFSCHREIBEN · Tablett ist abgedeckt' };
  return (
    <div className="space-y-4">
      <Card className="p-8 text-center" style={{ borderColor: phase === 'memorize' ? C.group : phase === 'write' ? C.alex : C.border }}>
        <div className="text-[10px] uppercase tracking-widest mb-3" style={{ color: phase === 'memorize' ? C.group : phase === 'write' ? C.alex : C.dim }}>{labels[phase]}</div>
        <Display className="text-7xl" style={{ color: phase === 'memorize' ? C.group : C.text }}>{phase === 'write' ? '⏸' : `${seconds}s`}</Display>
      </Card>
      <div className="flex gap-2">
        {phase === 'idle' && <Btn color="group" size="lg" onClick={() => { setSeconds(30); setPhase('memorize'); }} className="flex-1">Start (30s einprägen)</Btn>}
        {phase !== 'idle' && <Btn color="ghost" size="lg" onClick={() => { setSeconds(30); setPhase('idle'); }} className="flex-1">Reset</Btn>}
      </div>
    </div>
  );
};

const MolkkyTool = () => {
  const [alex, setAlex] = useState(0);
  const [grp, setGrp] = useState(0);
  const [hist, setHist] = useState([]);
  const add = (who, pts) => {
    const current = who === 'alex' ? alex : grp;
    const next = current + pts;
    const final = next > 50 ? 25 : next;
    setHist(h => [...h, { who, prev: current }]);
    if (who === 'alex') setAlex(final); else setGrp(final);
  };
  const undo = () => {
    if (!hist.length) return;
    const last = hist[hist.length - 1];
    if (last.who === 'alex') setAlex(last.prev); else setGrp(last.prev);
    setHist(h => h.slice(0, -1));
  };
  const reset = () => { setAlex(0); setGrp(0); setHist([]); };
  const renderRow = (who, score, color) => (
    <Card className="p-4" style={{ borderColor: score === 50 ? C.win : color }}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>{who === 'alex' ? 'Alex' : 'Herausforderer'}</div>
        <Display className="text-5xl" style={{ color: score === 50 ? C.win : color }}>{score}</Display>
      </div>
      <div className="grid grid-cols-6 gap-1">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
          <button key={n} onClick={() => add(who, n)} disabled={score === 50}
            className="rounded-md py-2 text-xs font-bold disabled:opacity-30 active:scale-95"
            style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }}>+{n}</button>
        ))}
      </div>
      {score === 50 && <div className="text-xs mt-2 font-bold text-center" style={{ color: C.win }}>GEWONNEN!</div>}
    </Card>
  );
  return (
    <div className="space-y-3">
      {renderRow('alex', alex, C.alex)}
      {renderRow('grp', grp, C.group)}
      <div className="flex gap-2">
        <Btn color="ghost" size="md" onClick={undo} disabled={!hist.length} className="flex-1">Undo</Btn>
        <Btn color="ghost" size="md" onClick={reset} className="flex-1">Reset</Btn>
      </div>
      <div className="text-xs text-center" style={{ color: C.muted }}>Genau 50 = Sieg · über 50 → Reset auf 25</div>
    </div>
  );
};

const NimTool = () => {
  const [stones, setStones] = useState(15);
  const [turn, setTurn] = useState('alex');
  const [done, setDone] = useState(false);
  const take = (n) => {
    if (done || n > stones) return;
    const next = stones - n;
    if (next === 0) { setDone(true); setStones(0); return; }
    setStones(next); setTurn(t => t === 'alex' ? 'grp' : 'alex');
  };
  const reset = () => { setStones(15); setTurn('alex'); setDone(false); };
  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="text-[10px] uppercase tracking-widest mb-3 text-center" style={{ color: C.dim }}>
          {done ? `${turn === 'alex' ? 'Alex' : 'Herausforderer'} hat den letzten genommen – VERLOREN` : `Am Zug: ${turn === 'alex' ? 'Alex' : 'Herausforderer'}`}
        </div>
        <div className="grid grid-cols-5 gap-2">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-lg flex items-center justify-center text-xs font-bold"
              style={{ background: i < stones ? (turn === 'alex' ? C.alex : C.group) : C.cardHi, color: i < stones ? (turn === 'alex' ? '#fff' : '#000') : C.dim, opacity: i < stones ? 1 : 0.3 }}>{i + 1}</div>
          ))}
        </div>
        <Display className="block text-center text-4xl mt-4" style={{ color: turn === 'alex' ? C.alex : C.group }}>{stones} übrig</Display>
      </Card>
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3].map(n => (
          <Btn key={n} color={turn === 'alex' ? 'alex' : 'group'} size="lg" onClick={() => take(n)} disabled={done || n > stones}>−{n}</Btn>
        ))}
      </div>
      <Btn color="ghost" size="md" onClick={reset} className="w-full">Reset</Btn>
    </div>
  );
};

const ReactionTool = () => {
  const [state, setState] = useState('idle');
  const [startMs, setStartMs] = useState(0);
  const [ms, setMs] = useState(null);
  const timerRef = useRef(null);
  const start = () => {
    setState('waiting'); setMs(null);
    timerRef.current = setTimeout(() => { setStartMs(Date.now()); setState('go'); }, 1500 + Math.random() * 3500);
  };
  const click = () => {
    if (state === 'idle') return start();
    if (state === 'waiting') { clearTimeout(timerRef.current); setState('early'); return; }
    if (state === 'go') { setMs(Date.now() - startMs); setState('result'); return; }
    return start();
  };
  const bg = state === 'idle' ? C.cardHi : state === 'waiting' ? C.alexDim : state === 'go' ? C.win : state === 'early' ? C.alex : C.cardHi;
  const label = state === 'idle' ? 'Tippen um zu starten' : state === 'waiting' ? 'WARTEN…' : state === 'go' ? 'JETZT!' : state === 'early' ? 'Zu früh!' : `${ms} ms`;
  return (
    <div className="space-y-3">
      <button onClick={click} className="w-full rounded-2xl p-12 font-black transition active:scale-[0.98]"
        style={{ background: bg, color: state === 'go' || state === 'early' ? '#000' : C.text, border: `1px solid ${C.border}` }}>
        <Display className="text-4xl block">{label}</Display>
      </button>
      <div className="text-xs text-center" style={{ color: C.muted }}>Tipp wenn der Hintergrund grün wird – nicht vorher!</div>
    </div>
  );
};

// Counter tool — generic counter for Liegestütze/Jonglieren/Ditschen
const CounterTool = () => {
  const [alex, setAlex] = useState(0);
  const [grp, setGrp] = useState(0);
  const [timer, setTimer] = useState(60);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (!running) return;
    if (timer <= 0) { setRunning(false); return; }
    const id = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [running, timer]);
  const startTimer = (sec) => { setTimer(sec); setRunning(true); };
  const reset = () => { setAlex(0); setGrp(0); setTimer(60); setRunning(false); };
  const winner = !running && (alex || grp) ? (alex > grp ? 'alex' : grp > alex ? 'grp' : 'tie') : null;
  return (
    <div className="space-y-3">
      <Card className="p-4 text-center" style={{ borderColor: running ? C.group : C.border }}>
        <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: C.dim }}>Timer</div>
        <Display className="text-5xl" style={{ color: timer <= 10 && running ? C.alex : C.text }}>{timer}s</Display>
        <div className="flex gap-2 mt-3 justify-center">
          <Btn color="neutral" size="sm" onClick={() => startTimer(30)} disabled={running}>30s</Btn>
          <Btn color="neutral" size="sm" onClick={() => startTimer(60)} disabled={running}>60s</Btn>
          <Btn color="neutral" size="sm" onClick={() => startTimer(120)} disabled={running}>120s</Btn>
          {running && <Btn color="ghost" size="sm" onClick={() => setRunning(false)}>Pause</Btn>}
        </div>
      </Card>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => setAlex(a => a + 1)} onContextMenu={e => { e.preventDefault(); setAlex(a => Math.max(0, a - 1)); }}
          className="rounded-2xl p-6 font-bold active:scale-95 transition" style={{ background: C.alex, color: '#fff' }}>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Alex · tap +1</div>
          <Display className="text-6xl block mt-1">{alex}</Display>
        </button>
        <button onClick={() => setGrp(g => g + 1)} onContextMenu={e => { e.preventDefault(); setGrp(g => Math.max(0, g - 1)); }}
          className="rounded-2xl p-6 font-bold active:scale-95 transition" style={{ background: C.group, color: '#000' }}>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Herausforderer · tap +1</div>
          <Display className="text-6xl block mt-1">{grp}</Display>
        </button>
      </div>
      {winner && (
        <Card className="p-3 text-center" style={{ borderColor: winner === 'alex' ? C.alex : winner === 'grp' ? C.group : C.border }}>
          <Display className="text-xl" style={{ color: winner === 'alex' ? C.alex : winner === 'grp' ? C.group : C.text }}>
            {winner === 'tie' ? 'GLEICHSTAND' : (winner === 'alex' ? 'ALEX' : 'HERAUSFORDERER') + ' SIEGT'}
          </Display>
        </Card>
      )}
      <div className="flex gap-2">
        <Btn color="ghost" size="md" onClick={reset} className="flex-1">Reset</Btn>
      </div>
      <div className="text-xs text-center" style={{ color: C.muted }}>Tap = +1 · Lang drücken / Rechtsklick = −1</div>
    </div>
  );
};

// W4 race tool — würfeln bis 20 (kumulativ)
const W4RaceTool = () => {
  const [alex, setAlex] = useState(0);
  const [grp, setGrp] = useState(0);
  const [lastRoll, setLastRoll] = useState(null);
  const [rollingFor, setRollingFor] = useState(null);
  const target = 20;
  const roll = (who) => {
    if (alex >= target || grp >= target) return;
    const r = 1 + Math.floor(Math.random() * 4);
    setLastRoll({ who, r });
    setRollingFor(who);
    if (who === 'alex') setAlex(a => a + r); else setGrp(g => g + r);
    setTimeout(() => setRollingFor(null), 400);
  };
  const reset = () => { setAlex(0); setGrp(0); setLastRoll(null); setRollingFor(null); };
  const winner = alex >= target ? 'alex' : grp >= target ? 'grp' : null;
  return (
    <div className="space-y-3">
      <Card className="p-4 text-center">
        <div className="text-[10px] uppercase tracking-widest" style={{ color: C.dim }}>Letzter W4-Wurf</div>
        <Display className="text-7xl block leading-none mt-1"
          style={{ color: lastRoll ? (lastRoll.who === 'alex' ? C.alex : C.group) : C.dim }}>
          {lastRoll ? lastRoll.r : '–'}
        </Display>
      </Card>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => roll('alex')} disabled={!!winner}
          className="rounded-2xl p-5 font-bold active:scale-95 transition disabled:opacity-50"
          style={{ background: C.alex, color: '#fff' }}>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Alex würfelt</div>
          <Display className="text-5xl block mt-1">{alex}</Display>
          <div className="text-xs opacity-80">Ziel: {target}</div>
        </button>
        <button onClick={() => roll('grp')} disabled={!!winner}
          className="rounded-2xl p-5 font-bold active:scale-95 transition disabled:opacity-50"
          style={{ background: C.group, color: '#000' }}>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Herausforderer würfelt</div>
          <Display className="text-5xl block mt-1">{grp}</Display>
          <div className="text-xs opacity-80">Ziel: {target}</div>
        </button>
      </div>
      {winner && (
        <Card className="p-3 text-center" style={{ borderColor: winner === 'alex' ? C.alex : C.group }}>
          <Display className="text-xl" style={{ color: winner === 'alex' ? C.alex : C.group }}>
            {winner === 'alex' ? 'ALEX' : 'HERAUSFORDERER'} ERREICHT 20
          </Display>
        </Card>
      )}
      <Btn color="ghost" size="md" onClick={reset} className="w-full">Reset</Btn>
      <div className="text-xs text-center" style={{ color: C.muted }}>Im Wechsel würfeln. Wer zuerst {target}+ erreicht, gewinnt.</div>
    </div>
  );
};

// ============================================================
//                   WER WIRD ALEX-IONÄR (Leader)
// ============================================================
const QuizMode = ({ shared, updateShared, onComplete }) => {
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [used, setUsed] = useState({ fifty: false, audience: false, phone: false });
  const [hidden, setHidden] = useState([]);
  const [phoneHint, setPhoneHint] = useState(null);
  const [done, setDone] = useState(null);
  const [voteSummary, setVoteSummary] = useState(null);

  const q = quizQuestions[step];
  const audienceActive = shared.activity?.type === 'audience-vote';
  const currentVotes = audienceActive ? Object.values(shared.votes?.[shared.activity.id] || {}) : [];
  const voteCount = currentVotes.length;

  const triggerAudience = () => {
    if (used.audience) return;
    const id = `q${step}-${Date.now()}`;
    updateShared(s => ({ ...s, activity: { type: 'audience-vote', id, qIdx: step, q: q.q, options: q.opts } }));
    setUsed(u => ({ ...u, audience: true }));
    setVoteSummary(null);
  };
  const closeAudience = () => {
    const tally = [0, 0, 0, 0];
    currentVotes.forEach(v => { if (typeof v.choice === 'number') tally[v.choice] += 1; });
    const total = tally.reduce((a, b) => a + b, 0) || 1;
    setVoteSummary(tally.map(c => Math.round((c / total) * 100)));
    updateShared(s => ({ ...s, activity: null }));
  };
  const useFifty = () => {
    if (used.fifty) return;
    const wrong = [0, 1, 2, 3].filter(i => i !== q.correct);
    setHidden(wrong.sort(() => Math.random() - 0.5).slice(0, 2));
    setUsed(u => ({ ...u, fifty: true }));
  };
  const usePhone = () => {
    if (used.phone) return;
    setPhoneHint('📞 ruft an …');
    setTimeout(() => {
      if (Math.random() < 0.75) setPhoneHint(`„Ich bin mir ziemlich sicher: ${q.opts[q.correct]}"`);
      else {
        const wrong = [0, 1, 2, 3].filter(i => i !== q.correct);
        setPhoneHint(`„Hmm, vielleicht ${q.opts[wrong[Math.floor(Math.random() * 3)]]}? Bin nicht sicher…"`);
      }
    }, 2000);
    setUsed(u => ({ ...u, phone: true }));
  };
  const lockIn = () => { if (picked !== null) setRevealed(true); };
  const next = () => {
    if (picked !== q.correct) { setDone({ level: step, won: false }); return; }
    if (step === quizQuestions.length - 1) { setDone({ level: step + 1, won: true }); return; }
    setStep(s => s + 1);
    setPicked(null); setRevealed(false); setHidden([]); setVoteSummary(null); setPhoneHint(null);
  };

  if (done) {
    const lastPrize = done.won ? quizQuestions[14].prize : done.level > 0 ? quizQuestions[done.level - 1].prize : '0 €';
    return (
      <div className="space-y-4">
        <Card className="p-8 text-center" style={{ borderColor: done.won ? C.win : C.alex }}>
          <Display className="text-5xl block mb-2" style={{ color: done.won ? C.win : C.alex }}>{done.won ? '🏆' : '💀'}</Display>
          <div className="text-xl font-bold mb-1">Alex erspielt:</div>
          <Display className="text-4xl block" style={{ color: C.group }}>{lastPrize}</Display>
          <div className="text-xs mt-2" style={{ color: C.muted }}>Frage {done.level} von 15</div>
        </Card>
        <Btn color={done.won ? 'alex' : 'group'} size="lg" className="w-full" onClick={() => onComplete(done.won ? 'alex' : 'challenger')}>
          Punkt für {done.won ? 'Alex' : 'Herausforderer'} – Quiz beenden
        </Btn>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: C.dim }}>Frage {step + 1} / 15</div>
          <Display className="text-3xl" style={{ color: C.group }}>{q.prize}</Display>
        </div>
        <div className="flex gap-2">
          <button onClick={useFifty} disabled={used.fifty} className="rounded-lg px-3 py-2 text-xs font-bold disabled:opacity-30"
            style={{ background: used.fifty ? C.cardHi : C.card, color: C.text, border: `1px solid ${used.fifty ? C.border : C.group}` }}>50:50</button>
          <button onClick={triggerAudience} disabled={used.audience} className="rounded-lg px-3 py-2 text-xs font-bold disabled:opacity-30"
            style={{ background: used.audience ? C.cardHi : C.card, color: C.text, border: `1px solid ${used.audience ? C.border : C.group}` }}>👥</button>
          <button onClick={usePhone} disabled={used.phone} className="rounded-lg px-3 py-2 text-xs font-bold disabled:opacity-30"
            style={{ background: used.phone ? C.cardHi : C.card, color: C.text, border: `1px solid ${used.phone ? C.border : C.group}` }}>📞</button>
        </div>
      </Card>
      <Card className="p-5"><div className="text-lg font-bold leading-snug">{q.q}</div></Card>
      {audienceActive && (
        <Card className="p-4" style={{ borderColor: C.group }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.group }}>Publikum stimmt ab</div>
            <div className="text-xs" style={{ color: C.muted }}>{voteCount} Stimmen</div>
          </div>
          <Btn color="group" size="md" className="w-full mt-2" onClick={closeAudience}>Auswerten</Btn>
        </Card>
      )}
      {voteSummary && !audienceActive && (
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.group }}>Publikumsjoker · Auswertung</div>
          <div className="space-y-1">
            {q.opts.map((opt, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <div className="w-4" style={{ color: C.muted }}>{['A', 'B', 'C', 'D'][i]}</div>
                <div className="flex-1 bg-zinc-800 rounded-sm h-3 overflow-hidden"><div style={{ width: `${voteSummary[i]}%`, background: C.group, height: '100%' }} /></div>
                <div className="w-8 text-right" style={{ color: C.muted }}>{voteSummary[i]}%</div>
              </div>
            ))}
          </div>
        </Card>
      )}
      {phoneHint && (
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: C.group }}>Telefonjoker</div>
          <div className="text-sm">{phoneHint}</div>
        </Card>
      )}
      <div className="space-y-2">
        {q.opts.map((opt, i) => {
          const isHidden = hidden.includes(i);
          const isPicked = picked === i;
          const isCorrect = revealed && i === q.correct;
          const isWrongPick = revealed && isPicked && i !== q.correct;
          let bg = C.card, border = C.border, color = C.text;
          if (isCorrect) { bg = C.win; color = '#000'; border = C.win; }
          else if (isWrongPick) { bg = C.alex; color = '#fff'; border = C.alex; }
          else if (isPicked) { bg = C.cardHi; border = C.group; }
          return (
            <button key={i} onClick={() => !revealed && setPicked(i)} disabled={isHidden || revealed}
              className="w-full text-left rounded-xl px-4 py-4 font-bold flex items-center gap-3 active:scale-[0.99] transition disabled:opacity-25"
              style={{ background: bg, border: `1px solid ${border}`, color }}>
              <div className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-black"
                style={{ background: isCorrect || isWrongPick ? 'rgba(0,0,0,0.2)' : C.cardHi, color: isCorrect ? '#000' : color }}>{['A', 'B', 'C', 'D'][i]}</div>
              <div className="flex-1">{opt}</div>
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        {!revealed && <Btn color="group" size="lg" onClick={lockIn} disabled={picked === null} className="flex-1">Loggen</Btn>}
        {revealed && <Btn color={picked === q.correct ? 'win' : 'alex'} size="lg" onClick={next} className="flex-1">
          {picked === q.correct ? (step === 14 ? 'Million abräumen!' : 'Weiter') : 'Beenden'}
        </Btn>}
      </div>
    </div>
  );
};

// ============================================================
//                  SCHÄTZFRAGE (Leader Side)
// ============================================================
const EstimateMode = ({ shared, updateShared }) => {
  const [prompt, setPrompt] = useState('');
  const [actual, setActual] = useState('');
  const activeId = shared.activity?.type === 'estimate' ? shared.activity.id : null;
  const submissions = useMemo(() => {
    if (!activeId) return [];
    return Object.entries(shared.estimates?.[activeId] || {}).map(([userId, v]) => ({ userId, ...v }));
  }, [activeId, shared.estimates]);
  const start = () => {
    if (!prompt.trim()) return;
    const id = `est-${Date.now()}`;
    updateShared(s => ({ ...s, activity: { type: 'estimate', id, prompt: prompt.trim(), actual: null } }));
  };
  const reveal = () => {
    if (actual === '') return;
    updateShared(s => ({ ...s, activity: { ...s.activity, actual: parseFloat(actual) } }));
  };
  const close = () => {
    updateShared(s => ({ ...s, activity: null }));
    setPrompt(''); setActual('');
  };
  const actualNum = parseFloat(actual);
  const sorted = !isNaN(actualNum)
    ? [...submissions].sort((a, b) => Math.abs(a.value - actualNum) - Math.abs(b.value - actualNum))
    : submissions;
  if (!shared.activity || shared.activity.type !== 'estimate') {
    return (
      <div className="space-y-4">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Frage stellen</div>
          <input value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Wie viele Schritte zum Brunnen?"
            className="w-full rounded-lg px-3 py-3 text-base"
            style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
        </Card>
        <Btn color="group" size="lg" onClick={start} disabled={!prompt.trim()} className="w-full">An alle senden</Btn>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <Card className="p-4" style={{ borderColor: C.group }}>
        <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: C.group }}>Aktive Frage · {submissions.length} Tipps</div>
        <div className="font-bold text-lg">{shared.activity.prompt}</div>
      </Card>
      <Card className="p-4">
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Echter Wert</div>
        <div className="flex gap-2">
          <input type="number" value={actual} onChange={e => setActual(e.target.value)} placeholder="z.B. 247"
            className="flex-1 rounded-lg px-3 py-3 text-base"
            style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
          <Btn color="group" size="md" onClick={reveal} disabled={actual === ''}>Auswerten</Btn>
        </div>
      </Card>
      {sorted.length > 0 && (
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>{!isNaN(actualNum) ? 'Rangliste' : 'Eingehende Tipps'}</div>
          <div className="space-y-1 max-h-80 overflow-auto">
            {sorted.map((t, i) => {
              const diff = !isNaN(actualNum) ? Math.abs(t.value - actualNum) : null;
              const isAlex = t.role === 'alex';
              return (
                <div key={t.userId || i} className="flex items-center gap-2 text-sm py-1.5"
                  style={{ borderBottom: i < sorted.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <div className="w-6 font-black" style={{ color: i === 0 && !isNaN(actualNum) ? C.win : C.muted, fontFamily: 'Anton, sans-serif' }}>{!isNaN(actualNum) ? i + 1 : '·'}</div>
                  <div className="flex-1 font-bold truncate" style={{ color: isAlex ? C.alex : C.text }}>{t.name}{isAlex ? ' ★' : ''}</div>
                  <div className="font-mono" style={{ color: C.muted }}>{t.value}</div>
                  {diff !== null && <div className="font-mono text-xs" style={{ color: diff === 0 ? C.win : C.dim }}>Δ{diff}</div>}
                </div>
              );
            })}
          </div>
        </Card>
      )}
      <Btn color="ghost" size="md" onClick={close} className="w-full">Schätzfrage beenden</Btn>
    </div>
  );
};

// ============================================================
//          AUDIENCE VOTE / ESTIMATE — Player prompts
// ============================================================
const PlayerVotePrompt = ({ activity, me, shared, send }) => {
  const myVote = shared.votes?.[activity.id]?.[me.userId];
  const [picked, setPicked] = useState(myVote ? myVote.choice : null);
  const submitted = myVote !== undefined;
  useEffect(() => { setPicked(myVote ? myVote.choice : null); }, [activity.id, myVote]);
  const submit = () => { if (picked === null) return; send({ type: 'vote', activityId: activity.id, choice: picked }); };
  return (
    <div className="space-y-4">
      <Card className="p-5" style={{ borderColor: C.group }}>
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.group }}>Publikumsjoker · Stimm ab!</div>
        <div className="text-lg font-bold leading-snug">{activity.q}</div>
      </Card>
      <div className="space-y-2">
        {activity.options.map((opt, i) => {
          const isPicked = picked === i;
          return (
            <button key={i} onClick={() => !submitted && setPicked(i)} disabled={submitted}
              className="w-full text-left rounded-xl px-4 py-4 font-bold flex items-center gap-3 active:scale-[0.99] transition disabled:opacity-50"
              style={{ background: isPicked ? C.cardHi : C.card, border: `1px solid ${isPicked ? C.group : C.border}`, color: C.text }}>
              <div className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-black"
                style={{ background: C.cardHi }}>{['A', 'B', 'C', 'D'][i]}</div>
              <div className="flex-1">{opt}</div>
            </button>
          );
        })}
      </div>
      {!submitted ? (
        <Btn color="group" size="lg" className="w-full" onClick={submit} disabled={picked === null}>Stimme abgeben</Btn>
      ) : (
        <Card className="p-4 text-center" style={{ borderColor: C.win }}>
          <Display className="text-2xl" style={{ color: C.win }}>✓ ABGESTIMMT</Display>
        </Card>
      )}
    </div>
  );
};

const PlayerEstimatePrompt = ({ activity, me, shared, send }) => {
  const myEstimate = shared.estimates?.[activity.id]?.[me.userId];
  const [value, setValue] = useState(myEstimate ? String(myEstimate.value) : '');
  const submitted = myEstimate !== undefined;
  useEffect(() => { setValue(myEstimate ? String(myEstimate.value) : ''); }, [activity.id, myEstimate]);
  const submit = () => { if (value === '') return; send({ type: 'estimate', activityId: activity.id, value: parseFloat(value) }); };
  return (
    <div className="space-y-4">
      <Card className="p-5" style={{ borderColor: C.group }}>
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.group }}>Schätzfrage</div>
        <div className="text-lg font-bold leading-snug">{activity.prompt}</div>
      </Card>
      {activity.actual !== null && activity.actual !== undefined ? (
        <Card className="p-4 text-center" style={{ borderColor: C.group }}>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: C.muted }}>Echter Wert</div>
          <Display className="text-5xl block" style={{ color: C.group }}>{activity.actual}</Display>
          {submitted && <div className="text-xs mt-2" style={{ color: C.muted }}>Dein Tipp: {value}</div>}
        </Card>
      ) : (
        <>
          <Card className="p-4">
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Dein Tipp</div>
            <input type="number" inputMode="numeric" value={value} onChange={e => setValue(e.target.value)}
              disabled={submitted} placeholder="Zahl eingeben"
              className="w-full rounded-lg px-3 py-4 text-2xl font-bold text-center"
              style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
          </Card>
          {!submitted ? (
            <Btn color="group" size="lg" className="w-full" onClick={submit} disabled={value === ''}>Tipp abschicken</Btn>
          ) : (
            <Card className="p-4 text-center" style={{ borderColor: C.win }}>
              <Display className="text-2xl" style={{ color: C.win }}>✓ ABGESCHICKT</Display>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================
//          FLAG QUIZ (Multi-User · 10 Flaggen, Auswertung am Ende)
// ============================================================
// Generic "10-question multi quiz" — used by flag-quiz and general-quiz.
const FlagQuizPlayer = ({ activity, me, shared, send }) => {
  const isFlag = activity.type === 'flag-quiz';
  const label = isFlag ? 'Flaggenrätsel' : 'Allgemeinwissen-Battle';
  const aid = activity.id;
  const qIdx = activity.current;
  const phase = activity.phase || 'playing';
  const questions = activity.questions || [];
  const total = questions.length;
  const myAns = shared.flagAnswers?.[aid]?.[qIdx]?.[me.userId];
  const [picked, setPicked] = useState(null);
  useEffect(() => { setPicked(null); }, [qIdx, aid]);
  if (!total) return <div className="p-4"><Card className="p-5 text-center"><div className="text-sm" style={{ color: C.muted }}>{label} wird vorbereitet…</div></Card></div>;
  const q = questions[Math.min(qIdx, total - 1)];
  const submit = (i) => {
    if (myAns !== undefined) return;
    setPicked(i);
    send({ type: 'flag-answer', activityId: aid, qIdx, choice: i });
  };

  if (phase === 'done') {
    // Evaluation per question
    const playerCount = Object.values(shared.flagAnswers?.[aid] || {}).reduce((m, q) => {
      Object.entries(q).forEach(([uid, a]) => { if (a.role === 'player') m.add(uid); });
      return m;
    }, new Set()).size;
    let alexHits = 0, groupHits = 0;
    activity.questions.forEach((qq, i) => {
      const answers = shared.flagAnswers?.[aid]?.[i] || {};
      const alexAns = Object.values(answers).find(a => a.role === 'alex');
      if (alexAns && alexAns.choice === qq.correct) alexHits++;
      // Group: majority of players right
      const playerAnswers = Object.values(answers).filter(a => a.role === 'player');
      const correctCount = playerAnswers.filter(a => a.choice === qq.correct).length;
      if (correctCount > playerAnswers.length / 2 && playerAnswers.length > 0) groupHits++;
    });
    return (
      <div className="space-y-4">
        <Card className="p-5 text-center">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>{label} · Auswertung</div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.alex }}>Alex</div>
              <Display className="text-5xl" style={{ color: C.alex }}>{alexHits}</Display>
              <div className="text-xs" style={{ color: C.muted }}>von {total}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.group }}>Gruppe</div>
              <Display className="text-5xl" style={{ color: C.group }}>{groupHits}</Display>
              <div className="text-xs" style={{ color: C.muted }}>von {total} (Mehrheit)</div>
            </div>
          </div>
        </Card>
        <Card className="p-3 text-xs space-y-1" style={{ color: C.muted }}>
          <div className="font-bold mb-2 text-xs uppercase tracking-widest" style={{ color: C.dim }}>Lösungen</div>
          {activity.questions.map((qq, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5">
              {isFlag ? <span className="text-lg">{qq.flag}</span> : <span className="font-mono w-6 text-right">{i + 1}.</span>}
              <span className="flex-1 truncate">{isFlag ? qq.opts[qq.correct] : qq.q}</span>
              {!isFlag && <span className="text-[10px] font-bold" style={{ color: C.win }}>{qq.opts[qq.correct]}</span>}
            </div>
          ))}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>{label} · {qIdx + 1} / {total}</div>
        <ModeBadge mode="multi" />
      </Card>
      {isFlag ? (
        <Card className="p-6 text-center">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Welches Land?</div>
          <div style={{ fontSize: '8rem', lineHeight: 1 }}>{q.flag}</div>
        </Card>
      ) : (
        <Card className="p-5"><div className="text-lg font-bold leading-snug">{q.q}</div></Card>
      )}
      <div className="space-y-2">
        {q.opts.map((opt, i) => {
          const isPicked = (myAns ? myAns.choice : picked) === i;
          const submitted = myAns !== undefined;
          return (
            <button key={i} onClick={() => submit(i)} disabled={submitted}
              className="w-full text-left rounded-xl px-4 py-4 font-bold flex items-center gap-3 active:scale-[0.99] transition disabled:opacity-60"
              style={{ background: isPicked ? C.cardHi : C.card, border: `1px solid ${isPicked ? C.multi : C.border}`, color: C.text }}>
              <div className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-black" style={{ background: C.cardHi }}>{['A','B','C','D'][i]}</div>
              <div className="flex-1">{opt}</div>
            </button>
          );
        })}
      </div>
      {myAns !== undefined && (
        <Card className="p-3 text-center text-xs" style={{ borderColor: C.win, color: C.win }}>
          ✓ Antwort gespeichert · warte auf nächste Flagge
        </Card>
      )}
    </div>
  );
};

const FlagQuizLeader = ({ activity, shared, updateShared, onComplete }) => {
  const isFlag = activity.type === 'flag-quiz';
  const label = isFlag ? 'Flaggenrätsel' : 'Allgemeinwissen';
  const aid = activity.id;
  const qIdx = activity.current;
  const phase = activity.phase || 'playing';
  const questions = activity.questions || [];
  const total = questions.length;
  if (!total) return <div className="p-4"><Card className="p-5 text-center"><div className="text-sm" style={{ color: C.muted }}>{label} wird vorbereitet…</div></Card></div>;
  const q = questions[Math.min(qIdx, total - 1)];

  const answersForCurrent = shared.flagAnswers?.[aid]?.[qIdx] || {};
  const playerAnswers = Object.values(answersForCurrent).filter(a => a.role === 'player');
  const alexAnswered = Object.values(answersForCurrent).some(a => a.role === 'alex');

  const next = () => {
    const newCurrent = qIdx + 1;
    if (newCurrent >= total) {
      updateShared(s => ({ ...s, activity: { ...s.activity, phase: 'done' } }));
    } else {
      updateShared(s => ({ ...s, activity: { ...s.activity, current: newCurrent } }));
    }
  };

  if (phase === 'done') {
    let alexHits = 0, groupHits = 0;
    activity.questions.forEach((qq, i) => {
      const answers = shared.flagAnswers?.[aid]?.[i] || {};
      const alexAns = Object.values(answers).find(a => a.role === 'alex');
      if (alexAns && alexAns.choice === qq.correct) alexHits++;
      const pAns = Object.values(answers).filter(a => a.role === 'player');
      const correct = pAns.filter(a => a.choice === qq.correct).length;
      if (correct > pAns.length / 2 && pAns.length > 0) groupHits++;
    });
    const winner = alexHits > groupHits ? 'alex' : groupHits > alexHits ? 'challenger' : 'tie';
    return (
      <div className="p-4 space-y-4 pb-24">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-widest mb-3" style={{ color: C.multi }}>{label} · Auswertung</div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="text-center">
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.alex }}>Alex</div>
              <Display className="text-5xl" style={{ color: C.alex }}>{alexHits}</Display>
            </div>
            <div className="text-center">
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.group }}>Gruppe (Mehrheit)</div>
              <Display className="text-5xl" style={{ color: C.group }}>{groupHits}</Display>
            </div>
          </div>
          <div className="space-y-1 max-h-64 overflow-auto pr-1">
            {activity.questions.map((qq, i) => {
              const answers = shared.flagAnswers?.[aid]?.[i] || {};
              const alexAns = Object.values(answers).find(a => a.role === 'alex');
              const pAns = Object.values(answers).filter(a => a.role === 'player');
              const correct = pAns.filter(a => a.choice === qq.correct).length;
              const aOk = alexAns?.choice === qq.correct;
              return (
                <div key={i} className="flex items-center gap-2 text-xs py-1.5 border-b" style={{ borderColor: C.border }}>
                  {isFlag ? <div className="text-lg">{qq.flag}</div> : <div className="font-mono w-6 text-right">{i + 1}.</div>}
                  <div className="flex-1 truncate">{isFlag ? qq.opts[qq.correct] : `${qq.q} → ${qq.opts[qq.correct]}`}</div>
                  <div className="font-mono" style={{ color: aOk ? C.win : C.alex }}>A: {alexAns ? (aOk ? '✓' : '✗') : '–'}</div>
                  <div className="font-mono" style={{ color: correct > pAns.length / 2 && pAns.length > 0 ? C.win : C.muted }}>G: {correct}/{pAns.length}</div>
                </div>
              );
            })}
          </div>
        </Card>
        <Card className="p-4 text-center" style={{ borderColor: winner === 'alex' ? C.alex : winner === 'challenger' ? C.group : C.border }}>
          <Display className="text-2xl block" style={{ color: winner === 'alex' ? C.alex : winner === 'challenger' ? C.group : C.text }}>
            {winner === 'tie' ? 'UNENTSCHIEDEN' : (winner === 'alex' ? 'ALEX' : 'GRUPPE') + ' GEWINNT'}
          </Display>
        </Card>
        <div className="grid grid-cols-2 gap-2">
          <Btn color="alex" size="lg" onClick={() => onComplete('alex')}>Punkt für Alex</Btn>
          <Btn color="group" size="lg" onClick={() => onComplete('challenger')}>Punkt für Gruppe</Btn>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>{label} · {qIdx + 1} / {total}</div>
        <ModeBadge mode="multi" />
      </Card>
      {isFlag ? (
        <Card className="p-6 text-center">
          <div style={{ fontSize: '8rem', lineHeight: 1 }}>{q.flag}</div>
          <div className="mt-2 text-sm font-bold" style={{ color: C.muted }}>Lösung: {q.opts[q.correct]}</div>
        </Card>
      ) : (
        <Card className="p-5">
          <div className="text-lg font-bold leading-snug">{q.q}</div>
          <div className="mt-2 text-sm font-bold" style={{ color: C.win }}>Lösung: {q.opts[q.correct]}</div>
        </Card>
      )}
      <Card className="p-3 text-xs flex items-center justify-around" style={{ color: C.muted }}>
        <span>Alex: <span style={{ color: alexAnswered ? C.win : C.dim }}>{alexAnswered ? '✓' : '…'}</span></span>
        <span>Spieler: <span style={{ color: C.text, fontWeight: 'bold' }}>{playerAnswers.length}</span></span>
      </Card>
      <Btn color="multi" size="lg" onClick={next} className="w-full">
        {qIdx + 1 >= total ? 'Auswerten' : 'Nächste Flagge'}
      </Btn>
      <Btn color="ghost" size="md" onClick={() => updateShared(s => ({ ...s, activity: null }))} className="w-full">Abbrechen</Btn>
    </div>
  );
};

// ============================================================
//          ALEX QUIZ LIVE (Multi-User · Alex tippt, alle sehen)
// ============================================================
const AlexQuizLivePlayer = ({ activity, me, shared, send }) => {
  const aid = activity.id;
  const qIdx = activity.current;
  const questions = activity.questions || [];
  const total = questions.length;
  const phase = activity.phase || 'playing';
  const alexAns = shared.alexAnswers?.[aid]?.[qIdx];
  const revealed = alexAns !== undefined;
  if (!total) return <div className="p-4"><Card className="p-5 text-center"><div className="text-sm" style={{ color: C.muted }}>Alex Quiz wird vorbereitet…</div></Card></div>;
  const q = questions[Math.min(qIdx, total - 1)];

  const submitAlex = (i) => {
    if (me.role !== 'alex') return;
    if (alexAns !== undefined) return;
    send({ type: 'alex-answer', activityId: aid, qIdx, choice: i });
  };

  if (phase === 'done') {
    let hits = 0;
    activity.questions.forEach((qq, i) => {
      const a = shared.alexAnswers?.[aid]?.[i];
      if (a && a.choice === qq.correct) hits++;
    });
    const pct = total > 0 ? Math.round((hits / total) * 100) : 0;
    return (
      <div className="space-y-4">
        <Card className="p-5 text-center">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Alex Quiz · Auswertung</div>
          <Display className="text-6xl block" style={{ color: pct > 50 ? C.alex : C.group }}>{hits} / {total}</Display>
          <div className="text-sm mt-2" style={{ color: C.muted }}>{pct}% richtig · {pct > 50 ? 'Alex bekommt den Punkt' : 'Gruppe bekommt den Punkt'}</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>Alex Quiz · {qIdx + 1} / {total}</div>
        <ModeBadge mode="multi" />
      </Card>
      <Card className="p-5"><div className="text-lg font-bold leading-snug">{q.q}</div></Card>
      <div className="space-y-2">
        {q.opts.map((opt, i) => {
          const isCorrect = revealed && i === q.correct;
          const isAlexPick = revealed && alexAns?.choice === i;
          const isWrongAlex = isAlexPick && !isCorrect;
          let bg = C.card, border = C.border, color = C.text;
          if (isCorrect) { bg = C.win; color = '#000'; border = C.win; }
          else if (isWrongAlex) { bg = C.alex; color = '#fff'; border = C.alex; }
          return (
            <button key={i} onClick={() => submitAlex(i)} disabled={me.role !== 'alex' || revealed}
              className="w-full text-left rounded-xl px-4 py-4 font-bold flex items-center gap-3 active:scale-[0.99] transition disabled:opacity-80"
              style={{ background: bg, border: `1px solid ${border}`, color }}>
              <div className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-black"
                style={{ background: isCorrect || isWrongAlex ? 'rgba(0,0,0,0.2)' : C.cardHi, color: isCorrect ? '#000' : color }}>{['A','B','C','D'][i]}</div>
              <div className="flex-1">{opt}</div>
              {isAlexPick && <span className="text-xs uppercase tracking-widest">Alex</span>}
            </button>
          );
        })}
      </div>
      {me.role === 'alex' && !revealed && (
        <div className="text-xs text-center" style={{ color: C.muted }}>Tippe deine Antwort an. Sobald du tippst, sehen alle die Lösung.</div>
      )}
      {me.role !== 'alex' && (
        <div className="text-xs text-center" style={{ color: C.muted }}>
          {revealed ? 'Alex hat geantwortet.' : 'Warte auf Alex…'}
        </div>
      )}
    </div>
  );
};

const AlexQuizLiveLeader = ({ activity, shared, updateShared, onComplete }) => {
  const aid = activity.id;
  const qIdx = activity.current;
  const questions = activity.questions || [];
  const total = questions.length;
  const phase = activity.phase || 'playing';
  const alexAns = shared.alexAnswers?.[aid]?.[qIdx];
  const revealed = alexAns !== undefined;
  if (!total) return <div className="p-4"><Card className="p-5 text-center"><div className="text-sm" style={{ color: C.muted }}>Alex Quiz wird vorbereitet…</div></Card></div>;
  const q = questions[Math.min(qIdx, total - 1)];

  const next = () => {
    if (qIdx + 1 >= total) {
      updateShared(s => ({ ...s, activity: { ...s.activity, phase: 'done' } }));
    } else {
      updateShared(s => ({ ...s, activity: { ...s.activity, current: qIdx + 1 } }));
    }
  };

  if (phase === 'done') {
    let hits = 0;
    activity.questions.forEach((qq, i) => {
      const a = shared.alexAnswers?.[aid]?.[i];
      if (a && a.choice === qq.correct) hits++;
    });
    const pct = total > 0 ? hits / total : 0;
    const winner = pct > 0.5 ? 'alex' : 'challenger';
    return (
      <div className="p-4 space-y-4 pb-24">
        <Card className="p-5 text-center">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Alex Quiz · Auswertung</div>
          <Display className="text-6xl block" style={{ color: winner === 'alex' ? C.alex : C.group }}>{hits} / {total}</Display>
          <div className="text-sm mt-2" style={{ color: C.muted }}>{Math.round(pct * 100)}% · {winner === 'alex' ? 'Alex' : 'Gruppe'} bekommt den Punkt</div>
        </Card>
        <Card className="p-3 text-xs space-y-1 max-h-64 overflow-auto" style={{ color: C.muted }}>
          {activity.questions.map((qq, i) => {
            const a = shared.alexAnswers?.[aid]?.[i];
            const ok = a?.choice === qq.correct;
            return (
              <div key={i} className="flex gap-2 py-1 border-b" style={{ borderColor: C.border }}>
                <div className="font-mono" style={{ color: ok ? C.win : C.alex }}>{ok ? '✓' : '✗'}</div>
                <div className="flex-1 truncate"><span style={{ color: C.text }}>{qq.q}</span> · richtig: {qq.opts[qq.correct]}</div>
              </div>
            );
          })}
        </Card>
        <div className="grid grid-cols-2 gap-2">
          <Btn color="alex" size="lg" onClick={() => onComplete('alex')} disabled={winner !== 'alex'}>Punkt für Alex</Btn>
          <Btn color="group" size="lg" onClick={() => onComplete('challenger')} disabled={winner !== 'challenger'}>Punkt für Gruppe</Btn>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>Alex Quiz · {qIdx + 1} / {total}</div>
        <div className="text-xs" style={{ color: C.muted }}>von {q.by || '?'}</div>
      </Card>
      <Card className="p-5"><div className="text-lg font-bold leading-snug">{q.q}</div></Card>
      <div className="space-y-2">
        {q.opts.map((opt, i) => {
          const isCorrect = i === q.correct;
          const isAlex = revealed && alexAns?.choice === i;
          return (
            <div key={i} className="rounded-xl px-4 py-3 flex items-center gap-3"
              style={{ background: revealed && isCorrect ? C.win : revealed && isAlex ? C.alex : C.card, border: `1px solid ${isCorrect ? C.win : C.border}`, color: revealed && (isCorrect || isAlex) ? '#000' : C.text }}>
              <div className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-black" style={{ background: 'rgba(0,0,0,0.2)' }}>{['A','B','C','D'][i]}</div>
              <div className="flex-1 font-bold">{opt}</div>
              <div className="text-xs">{isCorrect && '✓'} {isAlex && 'Alex'}</div>
            </div>
          );
        })}
      </div>
      <Btn color="multi" size="lg" onClick={next} disabled={!revealed} className="w-full">
        {qIdx + 1 >= total ? 'Auswerten' : 'Nächste Frage'}
      </Btn>
      {!revealed && <div className="text-xs text-center" style={{ color: C.muted }}>Alex muss erst antworten</div>}
      <Btn color="ghost" size="md" onClick={() => updateShared(s => ({ ...s, activity: null }))} className="w-full">Abbrechen</Btn>
    </div>
  );
};

// ============================================================
//          ALEX-QUIZ FRAGEN-EINREICHEN (Modal)
// ============================================================
// ============================================================
//          REAKTION-BATTLE (Multi)
// ============================================================
const ReactionBattlePlayer = ({ activity, me, shared, send }) => {
  const aid = activity.id;
  const phase = activity.phase;
  const myTap = shared.reactionTaps?.[aid]?.[me.userId];
  const onTap = () => {
    if (myTap) return;
    if (phase === 'wait') {
      // early tap = disqualify by sending fake huge ms
      send({ type: 'reaction-tap', activityId: aid }); // server only accepts in 'go'; nothing happens
    } else if (phase === 'go') {
      send({ type: 'reaction-tap', activityId: aid });
    }
  };
  const bg = phase === 'go' ? '#22c55e' : phase === 'wait' ? '#7f1d1d' : C.cardHi;
  const label = phase === 'go' ? 'JETZT!' : phase === 'wait' ? 'WARTEN…' : 'Auswerten…';
  return (
    <div className="space-y-4">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>Reaktion-Battle</div>
        <ModeBadge mode="multi" />
      </Card>
      <button onClick={onTap} disabled={phase === 'done' || !!myTap}
        className="w-full rounded-3xl p-16 font-black active:scale-[0.99] disabled:opacity-80"
        style={{ background: bg, color: phase === 'go' ? '#000' : '#fff', border: `1px solid ${C.border}`, minHeight: '40vh' }}>
        <Display className="text-5xl block">{label}</Display>
        {myTap && <div className="text-2xl mt-3">⚡ {myTap.ms} ms</div>}
      </button>
      <div className="text-xs text-center" style={{ color: C.muted }}>
        {phase === 'wait' ? 'Nicht zu früh tippen! Warten bis grün.' : phase === 'go' ? 'Tippen! So schnell wie möglich.' : myTap ? `Deine Zeit: ${myTap.ms} ms` : 'Warte auf Leader…'}
      </div>
    </div>
  );
};
const ReactionBattleLeader = ({ activity, shared, updateShared, onComplete }) => {
  const aid = activity.id;
  const phase = activity.phase;
  const taps = Object.entries(shared.reactionTaps?.[aid] || {}).map(([uid, t]) => ({ userId: uid, ...t }));
  const sorted = [...taps].sort((a, b) => a.ms - b.ms);
  const winner = sorted[0];
  const conclude = () => updateShared(s => ({ ...s, activity: { ...s.activity, phase: 'done' } }));
  const restart = () => {
    const id = 'rb-' + Date.now();
    updateShared(s => ({ ...s, activity: { type: 'reaction-battle', id, phase: 'wait', ts: Date.now() } }));
  };
  return (
    <div className="p-4 space-y-4 pb-24">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>Reaktion-Battle</div>
        <ModeBadge mode="multi" />
      </Card>
      <Card className="p-5 text-center" style={{ borderColor: phase === 'go' ? C.win : phase === 'wait' ? C.alex : C.border }}>
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>
          {phase === 'wait' ? 'Countdown läuft…' : phase === 'go' ? 'GRÜN — alle tippen jetzt!' : 'Ergebnis'}
        </div>
        <Display className="text-5xl" style={{ color: phase === 'go' ? C.win : phase === 'wait' ? C.alex : C.text }}>
          {phase === 'wait' ? 'WARTEN' : phase === 'go' ? 'JETZT' : 'STOP'}
        </Display>
      </Card>
      <Card className="p-3">
        <div className="text-[10px] uppercase tracking-widest mb-2 flex items-center justify-between" style={{ color: C.dim }}>
          <span>Reaktionszeiten · {taps.length}</span>
          {phase !== 'done' && taps.length > 0 && (
            <button onClick={conclude} className="text-xs font-bold px-2 py-1 rounded-md" style={{ background: C.multi, color: '#fff' }}>Auswerten</button>
          )}
        </div>
        <div className="space-y-1 max-h-72 overflow-auto">
          {sorted.length === 0 && <div className="text-xs" style={{ color: C.muted }}>Noch keine Reaktionen…</div>}
          {sorted.map((t, i) => (
            <div key={t.userId} className="flex items-center gap-2 text-sm py-1.5 border-b" style={{ borderColor: C.border }}>
              <div className="w-6 font-black" style={{ color: i === 0 ? C.win : C.muted }}>{i + 1}</div>
              <div className="flex-1 font-bold truncate" style={{ color: t.role === 'alex' ? C.alex : C.text }}>{t.name}{t.role === 'alex' ? ' ★' : ''}</div>
              <div className="font-mono" style={{ color: i === 0 ? C.win : C.muted }}>{t.ms} ms</div>
            </div>
          ))}
        </div>
      </Card>
      {phase === 'done' && winner && (
        <Card className="p-3 text-center" style={{ borderColor: winner.role === 'alex' ? C.alex : C.group }}>
          <div className="text-xs uppercase tracking-widest" style={{ color: C.muted }}>Sieger</div>
          <Display className="text-3xl" style={{ color: winner.role === 'alex' ? C.alex : C.group }}>{winner.name}</Display>
          <div className="text-xs mt-1" style={{ color: C.muted }}>{winner.ms} ms</div>
        </Card>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Btn color="alex" size="lg" onClick={() => onComplete('alex')}>Punkt für Alex</Btn>
        <Btn color="group" size="lg" onClick={() => onComplete('challenger')}>Punkt für Gruppe</Btn>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Btn color="neutral" size="md" onClick={restart}>↻ Neue Runde</Btn>
        <Btn color="ghost" size="md" onClick={() => updateShared(s => ({ ...s, activity: null }))}>Abbrechen</Btn>
      </div>
    </div>
  );
};

// ============================================================
//          WER WÜRDE EHER? (Multi-Vote)
// ============================================================
const WhoWouldRatherPlayer = ({ activity, me, shared, send }) => {
  const aid = activity.id;
  const myVote = shared.whoWouldVotes?.[aid]?.[me.userId];
  const submitted = myVote !== undefined;
  const submit = (i) => { if (!submitted) send({ type: 'who-vote', activityId: aid, choice: i }); };
  const phase = activity.phase || 'voting';

  if (phase === 'done') {
    const votes = Object.values(shared.whoWouldVotes?.[aid] || {});
    const tally = [0, 0];
    votes.forEach(v => { if (v.choice === 0 || v.choice === 1) tally[v.choice]++; });
    const total = votes.length;
    const pct = total ? [Math.round(tally[0] / total * 100), Math.round(tally[1] / total * 100)] : [0, 0];
    return (
      <div className="space-y-4">
        <Card className="p-5" style={{ borderColor: C.multi }}>
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.multi }}>Wer würde eher · Auswertung</div>
          <div className="text-lg font-bold mb-3">{activity.question}</div>
          {[0, 1].map(i => (
            <div key={i} className="mb-2">
              <div className="flex justify-between text-sm font-bold mb-1">
                <span style={{ color: i === 0 ? C.alex : C.group }}>{activity.options[i]}</span>
                <span style={{ color: C.muted }}>{tally[i]} ({pct[i]}%)</span>
              </div>
              <div className="h-4 rounded-md overflow-hidden" style={{ background: C.cardHi }}>
                <div style={{ width: `${pct[i]}%`, background: i === 0 ? C.alex : C.group, height: '100%' }} />
              </div>
            </div>
          ))}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>Wer würde eher…</div>
        <ModeBadge mode="multi" />
      </Card>
      <Card className="p-5"><div className="text-lg font-bold leading-snug">{activity.question}</div></Card>
      <div className="grid grid-cols-1 gap-3">
        {[0, 1].map(i => {
          const isPicked = myVote?.choice === i;
          return (
            <button key={i} onClick={() => submit(i)} disabled={submitted}
              className="rounded-2xl p-5 font-bold active:scale-95 disabled:opacity-60"
              style={{ background: isPicked ? (i === 0 ? C.alex : C.group) : C.cardHi, color: isPicked ? (i === 0 ? '#fff' : '#000') : C.text, border: `1px solid ${isPicked ? (i === 0 ? C.alex : C.group) : C.border}` }}>
              <Display className="text-3xl">{activity.options[i]}</Display>
            </button>
          );
        })}
      </div>
      {submitted && <Card className="p-3 text-center text-xs" style={{ borderColor: C.win, color: C.win }}>✓ Stimme abgegeben · warte auf Auswertung</Card>}
    </div>
  );
};
const WhoWouldRatherLeader = ({ activity, shared, presence, updateShared, onComplete }) => {
  const aid = activity.id;
  const phase = activity.phase || 'voting';
  const votes = Object.values(shared.whoWouldVotes?.[aid] || {});
  const tally = [0, 0];
  votes.forEach(v => { if (v.choice === 0 || v.choice === 1) tally[v.choice]++; });
  const conclude = () => updateShared(s => ({ ...s, activity: { ...s.activity, phase: 'done' } }));
  return (
    <div className="p-4 space-y-4 pb-24">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>Wer würde eher…</div>
        <ModeBadge mode="multi" />
      </Card>
      <Card className="p-5"><div className="text-lg font-bold leading-snug">{activity.question}</div></Card>
      <Card className="p-3">
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Live-Stimmen · {votes.length}</div>
        {[0, 1].map(i => (
          <div key={i} className="mb-2">
            <div className="flex justify-between text-sm font-bold mb-1">
              <span style={{ color: i === 0 ? C.alex : C.group }}>{activity.options[i]}</span>
              <span style={{ color: C.muted }}>{tally[i]}</span>
            </div>
            <div className="h-3 rounded-md overflow-hidden" style={{ background: C.cardHi }}>
              <div style={{ width: `${votes.length ? tally[i] / votes.length * 100 : 0}%`, background: i === 0 ? C.alex : C.group, height: '100%' }} />
            </div>
          </div>
        ))}
      </Card>
      {phase !== 'done' && <Btn color="multi" size="lg" onClick={conclude} className="w-full">Auswertung anzeigen</Btn>}
      <div className="grid grid-cols-2 gap-2">
        <Btn color="alex" size="lg" onClick={() => onComplete('alex')} disabled={!(tally[0] > tally[1])}>Punkt Alex</Btn>
        <Btn color="group" size="lg" onClick={() => onComplete('challenger')} disabled={!(tally[1] > tally[0])}>Punkt Gruppe</Btn>
      </div>
      <Btn color="ghost" size="md" onClick={() => updateShared(s => ({ ...s, activity: null }))} className="w-full">Abbrechen</Btn>
    </div>
  );
};

// Setup screen for "Wer würde eher" — leader picks question + 2 candidates
const WhoWouldRatherSetup = ({ presence, onStart, onCancel }) => {
  const [question, setQuestion] = useState('');
  const [b, setB] = useState('');
  const players = presence.filter(p => p.role === 'player');
  return (
    <div className="p-4 space-y-4 pb-24">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>🤔 Wer würde eher · Setup</div>
        <ModeBadge mode="multi" />
      </Card>
      <Card className="p-4">
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Frage</div>
        <input value={question} onChange={e => setQuestion(e.target.value)}
          placeholder={'z.B. „Wer kennt mehr Bundesliga-Vereine?"'}
          className="w-full rounded-lg px-3 py-3 text-base"
          style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
      </Card>
      <Card className="p-4">
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Option B (Option A = Alex)</div>
        <div className="space-y-1.5 max-h-60 overflow-auto">
          {players.length === 0 && <div className="text-xs" style={{ color: C.muted }}>Keine Mitspieler online. Fülle Option B per Hand:</div>}
          {players.map(p => (
            <button key={p.userId} onClick={() => setB(p.name)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg active:scale-95"
              style={{ background: b === p.name ? C.cardHi : C.card, border: `1px solid ${b === p.name ? C.group : C.border}` }}>
              <span className="w-2 h-2 rounded-full" style={{ background: p.active ? C.win : C.dim }} />
              <span className="flex-1 text-left font-bold text-sm">{p.name}</span>
            </button>
          ))}
        </div>
        <input value={b} onChange={e => setB(e.target.value)} placeholder="oder freier Name"
          className="w-full mt-2 rounded-lg px-3 py-2 text-sm"
          style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
      </Card>
      <div className="flex gap-2">
        <Btn color="ghost" size="lg" onClick={onCancel} className="flex-1">Abbrechen</Btn>
        <Btn color="multi" size="lg" onClick={() => onStart(question.trim(), b.trim())}
          disabled={!question.trim() || !b.trim()} className="flex-1">Starten</Btn>
      </div>
    </div>
  );
};

// ============================================================
//          BUZZER (Multi)
// ============================================================
const BuzzerPlayer = ({ activity, me, shared, send }) => {
  const aid = activity.id;
  const buzz = shared.buzzerState?.[aid];
  const isMe = buzz?.userId === me.userId;
  const onBuzz = () => { if (!buzz) send({ type: 'buzzer-buzz', activityId: aid }); };
  return (
    <div className="space-y-4">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>Buzzer-Runde</div>
        <ModeBadge mode="multi" />
      </Card>
      {activity.question && <Card className="p-5"><div className="text-lg font-bold leading-snug">{activity.question}</div></Card>}
      <button onClick={onBuzz} disabled={!!buzz}
        className="w-full rounded-3xl font-black active:scale-[0.97] disabled:opacity-80"
        style={{ background: buzz ? (isMe ? C.win : C.cardHi) : C.alex, color: buzz ? (isMe ? '#000' : C.muted) : '#fff', minHeight: '40vh', border: `1px solid ${C.border}` }}>
        <Display className="text-6xl block">{buzz ? (isMe ? 'DU! 🎉' : `${buzz.name}`) : 'BUZZ!'}</Display>
        {!buzz && <div className="text-sm mt-2 opacity-80">Tippen wenn du die Antwort weißt</div>}
      </button>
    </div>
  );
};
const BuzzerLeader = ({ activity, shared, updateShared, onComplete }) => {
  const aid = activity.id;
  const buzz = shared.buzzerState?.[aid];
  const [questionDraft, setQuestionDraft] = useState(activity.question || '');
  useEffect(() => setQuestionDraft(activity.question || ''), [activity.id]);
  const updateQuestion = () => {
    updateShared(s => ({ ...s, activity: { ...s.activity, question: questionDraft.trim() } }));
  };
  const reset = () => {
    // new buzzer activity id → wipes buzzer state
    const id = 'bz-' + Date.now();
    updateShared(s => ({ ...s, activity: { type: 'buzzer', id, question: questionDraft.trim(), ts: Date.now() } }));
  };
  return (
    <div className="p-4 space-y-4 pb-24">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>Buzzer-Runde</div>
        <ModeBadge mode="multi" />
      </Card>
      <Card className="p-4">
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Frage (optional — kannst du auch nur mündlich stellen)</div>
        <textarea value={questionDraft} onChange={e => setQuestionDraft(e.target.value)} rows={2}
          className="w-full rounded-lg px-3 py-2 text-sm resize-none"
          style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
        <div className="flex gap-2 mt-2">
          <Btn color="neutral" size="sm" onClick={updateQuestion}>Frage übernehmen</Btn>
          <Btn color="multi" size="sm" onClick={reset}>↻ Neue Runde (Buzzer reset)</Btn>
        </div>
      </Card>
      <Card className="p-5 text-center" style={{ borderColor: buzz ? C.win : C.border }}>
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Buzzer</div>
        {buzz ? (
          <>
            <Display className="text-4xl block" style={{ color: buzz.role === 'alex' ? C.alex : C.win }}>{buzz.name}</Display>
            <div className="text-xs mt-1" style={{ color: C.muted }}>{buzz.role === 'alex' ? 'Alex' : 'Mitspieler'} · darf antworten</div>
          </>
        ) : (
          <Display className="text-2xl block" style={{ color: C.dim }}>Wartet auf Buzz…</Display>
        )}
      </Card>
      <div className="grid grid-cols-2 gap-2">
        <Btn color="alex" size="lg" onClick={() => onComplete('alex')}>Punkt für Alex</Btn>
        <Btn color="group" size="lg" onClick={() => onComplete('challenger')}>Punkt für Gruppe</Btn>
      </div>
      <Btn color="ghost" size="md" onClick={() => updateShared(s => ({ ...s, activity: null }))} className="w-full">Abbrechen</Btn>
    </div>
  );
};

// ============================================================
//          SCHÄTZ-BATTLE (Multi · Alex vs. Gruppen-Durchschnitt)
// ============================================================
function avgRoundEval(roundTips, actual) {
  const tips = Object.values(roundTips || {});
  const alexTip = tips.find(t => t.role === 'alex');
  const playerTips = tips.filter(t => t.role === 'player');
  const groupAvg = playerTips.length ? playerTips.reduce((s, t) => s + t.value, 0) / playerTips.length : null;
  let winner = null;
  if (actual !== null && actual !== undefined && !isNaN(actual)) {
    const alexDist = alexTip ? Math.abs(alexTip.value - actual) : null;
    const groupDist = groupAvg !== null ? Math.abs(groupAvg - actual) : null;
    if (alexDist === null && groupDist === null) winner = 'tie';
    else if (alexDist === null) winner = 'challenger';
    else if (groupDist === null) winner = 'alex';
    else if (alexDist < groupDist) winner = 'alex';
    else if (alexDist > groupDist) winner = 'challenger';
    else winner = 'tie';
  }
  return { alexValue: alexTip?.value ?? null, groupAvg, alexCount: alexTip ? 1 : 0, playerCount: playerTips.length, winner };
}

const AvgEstimatePlayer = ({ activity, me, shared, send }) => {
  const aid = activity.id;
  const roundIdx = (activity.rounds || []).length;
  const phase = activity.phase || 'asking';
  const tips = shared.avgEstimateTips?.[aid]?.[roundIdx] || {};
  const myTip = tips[me.userId];
  const [value, setValue] = useState(myTip ? String(myTip.value) : '');
  useEffect(() => { setValue(''); }, [aid, roundIdx]);

  const submit = () => {
    if (value === '' || isNaN(parseFloat(value))) return;
    send({ type: 'avg-estimate-tip', activityId: aid, value: parseFloat(value) });
  };

  if (phase === 'done') {
    let alexWins = 0, groupWins = 0;
    (activity.rounds || []).forEach(r => { if (r.winner === 'alex') alexWins++; else if (r.winner === 'challenger') groupWins++; });
    return (
      <div className="space-y-4">
        <Card className="p-5 text-center">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Schätz-Battle · Auswertung</div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.alex }}>Alex</div><Display className="text-5xl" style={{ color: C.alex }}>{alexWins}</Display></div>
            <div><div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.group }}>Gruppe</div><Display className="text-5xl" style={{ color: C.group }}>{groupWins}</Display></div>
          </div>
          <div className="text-xs mt-2" style={{ color: C.muted }}>{(activity.rounds || []).length} Runden gespielt</div>
        </Card>
      </div>
    );
  }

  const showReveal = phase === 'revealed' && activity.actual !== null && activity.actual !== undefined;
  const evalRes = showReveal ? avgRoundEval(tips, activity.actual) : null;

  return (
    <div className="space-y-4">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>📊 Schätz-Battle · Runde {roundIdx + 1}</div>
        <ModeBadge mode="multi" />
      </Card>
      {activity.prompt ? (
        <Card className="p-5"><div className="text-lg font-bold leading-snug">{activity.prompt}</div></Card>
      ) : (
        <Card className="p-5 text-center"><div className="text-sm" style={{ color: C.muted }}>Spielleiter stellt gleich die Frage…</div></Card>
      )}
      {showReveal ? (
        <Card className="p-4 text-center" style={{ borderColor: evalRes.winner === 'alex' ? C.alex : evalRes.winner === 'challenger' ? C.group : C.border }}>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: C.muted }}>Echter Wert</div>
          <Display className="text-4xl block mb-2" style={{ color: C.win }}>{activity.actual}</Display>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: C.alex }}>Alex</div>
              <div className="font-mono text-xl" style={{ color: C.alex }}>{evalRes.alexValue ?? '–'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: C.group }}>Gruppen-Ø ({evalRes.playerCount})</div>
              <div className="font-mono text-xl" style={{ color: C.group }}>{evalRes.groupAvg !== null ? evalRes.groupAvg.toFixed(1) : '–'}</div>
            </div>
          </div>
          <Display className="text-xl block mt-3" style={{ color: evalRes.winner === 'alex' ? C.alex : evalRes.winner === 'challenger' ? C.group : C.muted }}>
            {evalRes.winner === 'alex' ? 'ALEX näher dran' : evalRes.winner === 'challenger' ? 'GRUPPE näher dran' : 'Unentschieden'}
          </Display>
          {myTip && <div className="text-xs mt-2" style={{ color: C.muted }}>Dein Tipp: {myTip.value}</div>}
        </Card>
      ) : (
        <>
          <Card className="p-4">
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Dein Tipp</div>
            <input type="number" inputMode="decimal" value={myTip ? String(myTip.value) : value}
              onChange={e => setValue(e.target.value)} disabled={!!myTip || !activity.prompt}
              placeholder="Zahl eingeben"
              className="w-full rounded-lg px-3 py-4 text-2xl font-bold text-center"
              style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
          </Card>
          {!myTip ? (
            <Btn color="multi" size="lg" className="w-full" onClick={submit} disabled={!activity.prompt || value === ''}>Tipp abschicken</Btn>
          ) : (
            <Card className="p-3 text-center text-xs" style={{ borderColor: C.win, color: C.win }}>✓ {myTip.value} abgeschickt · warte auf Auflösung</Card>
          )}
        </>
      )}
    </div>
  );
};

const AvgEstimateLeader = ({ activity, shared, updateShared, onComplete }) => {
  const aid = activity.id;
  const roundIdx = (activity.rounds || []).length;
  const phase = activity.phase || 'asking';
  const tips = shared.avgEstimateTips?.[aid]?.[roundIdx] || {};
  const [prompt, setPrompt] = useState(activity.prompt || '');
  const [actual, setActual] = useState('');
  useEffect(() => { setPrompt(activity.prompt || ''); setActual(''); }, [aid, roundIdx, activity.prompt]);

  const askQuestion = () => {
    if (!prompt.trim()) return;
    updateShared(s => ({ ...s, activity: { ...s.activity, prompt: prompt.trim(), actual: null, phase: 'asking' } }));
  };
  const reveal = () => {
    if (actual === '' || isNaN(parseFloat(actual))) return;
    updateShared(s => ({ ...s, activity: { ...s.activity, actual: parseFloat(actual), phase: 'revealed' } }));
  };
  const nextRound = () => {
    const evalRes = avgRoundEval(tips, activity.actual);
    const roundEntry = { prompt: activity.prompt, actual: activity.actual, alexValue: evalRes.alexValue, groupAvg: evalRes.groupAvg, winner: evalRes.winner, ts: Date.now() };
    updateShared(s => ({ ...s, activity: { ...s.activity, rounds: [...(s.activity.rounds || []), roundEntry], prompt: '', actual: null, phase: 'asking' } }));
  };
  const finishGame = () => {
    // Auch aktuelle Runde mit übernehmen wenn revealed
    if (phase === 'revealed') {
      const evalRes = avgRoundEval(tips, activity.actual);
      const roundEntry = { prompt: activity.prompt, actual: activity.actual, alexValue: evalRes.alexValue, groupAvg: evalRes.groupAvg, winner: evalRes.winner, ts: Date.now() };
      updateShared(s => ({ ...s, activity: { ...s.activity, rounds: [...(s.activity.rounds || []), roundEntry], phase: 'done' } }));
    } else {
      updateShared(s => ({ ...s, activity: { ...s.activity, phase: 'done' } }));
    }
  };

  if (phase === 'done') {
    let alexWins = 0, groupWins = 0;
    (activity.rounds || []).forEach(r => { if (r.winner === 'alex') alexWins++; else if (r.winner === 'challenger') groupWins++; });
    const tendency = alexWins > groupWins ? 'alex' : groupWins > alexWins ? 'challenger' : 'tie';
    return (
      <div className="p-4 space-y-4 pb-24">
        <Card className="p-5 text-center">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Schätz-Battle · Auswertung</div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.alex }}>Alex</div><Display className="text-5xl" style={{ color: C.alex }}>{alexWins}</Display></div>
            <div><div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.group }}>Gruppe</div><Display className="text-5xl" style={{ color: C.group }}>{groupWins}</Display></div>
          </div>
          <div className="text-xs mt-2" style={{ color: C.muted }}>{(activity.rounds || []).length} Runden</div>
        </Card>
        <Card className="p-3 text-xs space-y-1 max-h-64 overflow-auto" style={{ color: C.muted }}>
          {(activity.rounds || []).map((r, i) => (
            <div key={i} className="flex items-center gap-2 py-1 border-b" style={{ borderColor: C.border }}>
              <div className="font-mono w-6 text-right">{i + 1}.</div>
              <div className="flex-1 truncate">{r.prompt}</div>
              <div className="font-mono" style={{ color: C.alex }}>{r.alexValue ?? '–'}</div>
              <div className="font-mono" style={{ color: C.group }}>{r.groupAvg !== null ? r.groupAvg.toFixed(1) : '–'}</div>
              <div className="font-mono" style={{ color: C.win }}>={r.actual}</div>
              <div className="text-[10px] font-bold uppercase" style={{ color: r.winner === 'alex' ? C.alex : r.winner === 'challenger' ? C.group : C.muted }}>{r.winner === 'alex' ? 'A' : r.winner === 'challenger' ? 'G' : '·'}</div>
            </div>
          ))}
        </Card>
        <div className="grid grid-cols-2 gap-2">
          <Btn color="alex" size="lg" onClick={() => onComplete('alex')}>Punkt für Alex{tendency === 'alex' ? ' ★' : ''}</Btn>
          <Btn color="group" size="lg" onClick={() => onComplete('challenger')}>Punkt für Gruppe{tendency === 'challenger' ? ' ★' : ''}</Btn>
        </div>
        <Btn color="ghost" size="md" onClick={() => updateShared(s => ({ ...s, activity: null }))} className="w-full">Abbrechen ohne Punkt</Btn>
      </div>
    );
  }

  const liveTips = Object.values(tips);
  const alexTip = liveTips.find(t => t.role === 'alex');
  const playerTips = liveTips.filter(t => t.role === 'player');
  const showReveal = phase === 'revealed';
  const evalRes = showReveal ? avgRoundEval(tips, activity.actual) : null;

  return (
    <div className="p-4 space-y-4 pb-24">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>📊 Schätz-Battle · Runde {roundIdx + 1}</div>
        <ModeBadge mode="multi" />
      </Card>

      {!activity.prompt ? (
        <>
          <Card className="p-4">
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Frage stellen</div>
            <input value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Wie viele Schritte zum Brunnen?"
              className="w-full rounded-lg px-3 py-3 text-base"
              style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
          </Card>
          <Btn color="multi" size="lg" onClick={askQuestion} disabled={!prompt.trim()} className="w-full">An alle senden</Btn>
        </>
      ) : (
        <Card className="p-4" style={{ borderColor: C.group }}>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: C.group }}>Aktuelle Frage</div>
          <div className="font-bold text-lg">{activity.prompt}</div>
        </Card>
      )}

      {activity.prompt && !showReveal && (
        <>
          <Card className="p-3 text-xs flex items-center justify-around" style={{ color: C.muted }}>
            <span>Alex: <span style={{ color: alexTip ? C.win : C.dim, fontWeight: 'bold' }}>{alexTip ? '✓' : '…'}</span></span>
            <span>Spieler: <span style={{ color: C.text, fontWeight: 'bold' }}>{playerTips.length}</span></span>
          </Card>
          <Card className="p-4">
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Echter Wert</div>
            <div className="flex gap-2">
              <input type="number" value={actual} onChange={e => setActual(e.target.value)} placeholder="z.B. 247"
                className="flex-1 rounded-lg px-3 py-3 text-base"
                style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
              <Btn color="group" size="md" onClick={reveal} disabled={actual === ''}>Auflösen</Btn>
            </div>
          </Card>
        </>
      )}

      {showReveal && (
        <Card className="p-4 text-center" style={{ borderColor: evalRes.winner === 'alex' ? C.alex : evalRes.winner === 'challenger' ? C.group : C.border }}>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: C.muted }}>Echter Wert</div>
          <Display className="text-4xl block mb-2" style={{ color: C.win }}>{activity.actual}</Display>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: C.alex }}>Alex</div>
              <div className="font-mono text-2xl" style={{ color: C.alex }}>{evalRes.alexValue ?? '–'}</div>
              <div className="text-[10px]" style={{ color: C.muted }}>Δ {evalRes.alexValue !== null ? Math.abs(evalRes.alexValue - activity.actual).toFixed(1) : '–'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: C.group }}>Gruppen-Ø ({evalRes.playerCount})</div>
              <div className="font-mono text-2xl" style={{ color: C.group }}>{evalRes.groupAvg !== null ? evalRes.groupAvg.toFixed(1) : '–'}</div>
              <div className="text-[10px]" style={{ color: C.muted }}>Δ {evalRes.groupAvg !== null ? Math.abs(evalRes.groupAvg - activity.actual).toFixed(1) : '–'}</div>
            </div>
          </div>
          <Display className="text-2xl block mt-3" style={{ color: evalRes.winner === 'alex' ? C.alex : evalRes.winner === 'challenger' ? C.group : C.muted }}>
            {evalRes.winner === 'alex' ? 'ALEX gewinnt Runde' : evalRes.winner === 'challenger' ? 'GRUPPE gewinnt Runde' : 'Unentschieden'}
          </Display>
        </Card>
      )}

      {(activity.rounds || []).length > 0 && (
        <Card className="p-3">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Bisherige Runden</div>
          <div className="space-y-1 text-xs">
            {(activity.rounds || []).map((r, i) => (
              <div key={i} className="flex items-center gap-2 py-1 border-b" style={{ borderColor: C.border }}>
                <div className="font-mono w-6 text-right">{i + 1}.</div>
                <div className="flex-1 truncate" style={{ color: C.muted }}>{r.prompt}</div>
                <div className="text-[10px] font-bold uppercase" style={{ color: r.winner === 'alex' ? C.alex : r.winner === 'challenger' ? C.group : C.muted }}>{r.winner === 'alex' ? 'Alex' : r.winner === 'challenger' ? 'Gruppe' : '–'}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-2">
        {showReveal && <Btn color="multi" size="lg" onClick={nextRound}>Nächste Runde</Btn>}
        <Btn color={showReveal ? 'group' : 'ghost'} size="lg" onClick={finishGame}>Spiel beenden</Btn>
      </div>
      <Btn color="ghost" size="md" onClick={() => updateShared(s => ({ ...s, activity: null }))} className="w-full">Abbrechen</Btn>
    </div>
  );
};

// ============================================================
//          MONTAGSMALER / PICTIONARY (Multi)
// ============================================================
const PictionaryPlayer = ({ activity, me, strokes, send }) => {
  const isDrawer = activity.drawerId === me.userId;
  const [color, setColor] = useState('#000000');
  const [size, setSize] = useState(4);
  const word = activity.word;
  const wordLen = activity.wordLen || (word?.length || 0);
  const hint = isDrawer
    ? `Du malst: ${word || '…'}`
    : `Gemalt von ${activity.drawerName || '?'} · ${wordLen} Buchstaben · raten im Chat`;
  return (
    <div className="space-y-3 p-4 pb-24">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>
          🎨 Montagsmaler · Runde {activity.round || 1}
        </div>
        <ModeBadge mode="multi" />
      </Card>
      {isDrawer && (
        <Card className="p-4 text-center" style={{ borderColor: C.win }}>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: C.dim }}>Dein Wort</div>
          <Display className="text-4xl" style={{ color: C.win }}>{word || '?'}</Display>
        </Card>
      )}
      {!isDrawer && (
        <Card className="p-3 text-center" style={{ borderColor: C.border }}>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: C.dim }}>
            {activity.drawerName} malt · {wordLen} Buchstaben
          </div>
          <div className="text-xs mt-1" style={{ color: C.muted }}>Rufe deinen Tipp rein oder schreib in den Chat 💬</div>
        </Card>
      )}
      <div className="rounded-lg overflow-hidden" style={{ height: '50vh', border: `1px solid ${C.border}` }}>
        <DrawingBoard strokes={strokes} canDraw={isDrawer} send={send}
          onClear={() => send({ type: 'draw-clear' })}
          color={color} setColor={setColor} size={size} setSize={setSize} hint={isDrawer ? null : hint} />
      </div>
    </div>
  );
};

const PictionaryLeader = ({ activity, shared, strokes, send, updateShared, onComplete }) => {
  const [color, setColor] = useState('#000000');
  const [size, setSize] = useState(4);
  const word = activity.word;
  const rolls = activity.rounds || [];
  return (
    <div className="space-y-3 p-4 pb-24">
      <Card className="p-3 flex items-center justify-between" style={{ borderColor: C.multi }}>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.multi }}>
          🎨 Montagsmaler · Runde {activity.round || 1}
        </div>
        <ModeBadge mode="multi" />
      </Card>
      <Card className="p-3">
        <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: C.dim }}>
          Zeichner: <span style={{ color: activity.drawerRole === 'alex' ? C.alex : C.group, fontWeight: 'bold' }}>{activity.drawerName || '–'}</span>
        </div>
        <Display className="text-2xl block" style={{ color: C.win }}>{word || '?'}</Display>
      </Card>
      <div className="rounded-lg overflow-hidden" style={{ height: '45vh', border: `1px solid ${C.border}` }}>
        <DrawingBoard strokes={strokes} canDraw={false} send={send}
          onClear={() => send({ type: 'draw-clear' })}
          color={color} setColor={setColor} size={size} setSize={setSize} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Btn color="alex" size="md" onClick={() => send({ type: 'pictionary-round-end', who: 'alex' })}>Alex hat erraten</Btn>
        <Btn color="group" size="md" onClick={() => send({ type: 'pictionary-round-end', who: 'challenger' })}>Gruppe hat erraten</Btn>
      </div>
      <Btn color="multi" size="lg" onClick={() => send({ type: 'pictionary-roll-drawer' })} className="w-full">🎲 Nächster Zeichner + neues Wort</Btn>
      {rolls.length > 0 && (
        <Card className="p-3">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Runden · {rolls.length}</div>
          <div className="space-y-1 max-h-40 overflow-auto">
            {rolls.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-1 border-b" style={{ borderColor: C.border }}>
                <div className="w-6 font-black opacity-50">#{i + 1}</div>
                <div className="flex-1 truncate"><span style={{ color: C.muted }}>{r.drawerName}</span> · {r.word}</div>
                {r.winner && <div className="text-[10px] font-bold uppercase" style={{ color: r.winner === 'alex' ? C.alex : C.group }}>
                  {r.winner === 'alex' ? 'Alex' : 'Gruppe'}
                </div>}
              </div>
            ))}
          </div>
        </Card>
      )}
      <div className="text-center text-xs pt-2" style={{ color: C.muted }}>Spiel beenden mit Sieger:</div>
      <div className="grid grid-cols-2 gap-2">
        <Btn color="alex" size="lg" onClick={() => onComplete('alex')}>Punkt für Alex</Btn>
        <Btn color="group" size="lg" onClick={() => onComplete('challenger')}>Punkt für Gruppe</Btn>
      </div>
      <Btn color="ghost" size="md" onClick={() => updateShared(s => ({ ...s, activity: null }))} className="w-full">Abbrechen</Btn>
    </div>
  );
};

// ============================================================
//          POOL CARD (visible to all roles)
// ============================================================
const PoolCard = ({ shared, scores }) => {
  const all = [...games, ...(shared.customGames || [])];
  const history = shared.history || [];
  const playedIds = new Set(history.map(h => h.gameId));
  const playedById = new Map(history.map((h, i) => [h.gameId, { ...h, idx: i }]));
  const totalPlayed = history.length;
  const nextPoints = totalPlayed + 1;
  const pending = all.filter(g => !playedIds.has(g.id));
  const played  = all.filter(g => playedIds.has(g.id));
  return (
    <Card className="p-4">
      <div className="text-[10px] uppercase tracking-widest mb-3 flex items-center justify-between" style={{ color: C.dim }}>
        <span>Spielepool</span>
        <span style={{ color: C.group }}>Nächstes: #{nextPoints} · {nextPoints} P</span>
      </div>
      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest mb-1 mt-1" style={{ color: C.muted }}>Ausstehend · {pending.length}</div>
        {pending.map(g => (
          <div key={g.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ background: C.cardHi }}>
            <div className="text-lg">{g.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{g.name}</div>
              <div className="text-[10px] truncate" style={{ color: C.muted }}>{g.short}</div>
              {g.by && <div className="text-[9px] truncate" style={{ color: C.group }}>↑ vorgeschlagen von {g.by}</div>}
            </div>
            <ModeBadge mode={g.mode || 'live'} />
          </div>
        ))}
        {played.length > 0 && (
          <div className="text-[10px] uppercase tracking-widest mb-1 mt-3" style={{ color: C.muted }}>Gespielt · {played.length}</div>
        )}
        {played.map(g => {
          const h = playedById.get(g.id);
          return (
            <div key={g.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ background: C.cardHi, opacity: 0.6 }}>
              <div className="text-lg grayscale">{g.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{g.name}</div>
                <div className="text-[10px] truncate" style={{ color: C.muted }}>
                  #{h.idx + 1} · {h.winner === 'alex' ? 'Alex' : 'Gruppe'} +{h.points || h.idx + 1}
                </div>
              </div>
              <div className="text-xs font-bold" style={{ color: h.winner === 'alex' ? C.alex : C.group }}>
                +{h.points || h.idx + 1}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const AlexQuestionSubmit = ({ open, onClose, send, pool, me }) => {
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState(['', '', '', '']);
  const [correct, setCorrect] = useState(0);
  const reset = () => { setQ(''); setOpts(['','','','']); setCorrect(0); };
  const submit = () => {
    if (!q.trim()) return;
    if (opts.some(o => !o.trim())) return;
    send({ type: 'submit-alex-question', q: q.trim(), opts: opts.map(o => o.trim()), correct });
    reset();
  };
  return (
    <Modal open={open} onClose={onClose} title={`Frage einreichen · ${pool.length} im Pool`}>
      <div className="space-y-3">
        <Card className="p-3">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Frage rund um Alex</div>
          <textarea value={q} onChange={e => setQ(e.target.value)} maxLength={240}
            placeholder={'z.B. „In welcher Stadt hat Alex studiert?"'} rows={3}
            className="w-full rounded-lg px-3 py-2 text-sm resize-none"
            style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
        </Card>
        <Card className="p-3">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>4 Antworten · markiere die richtige</div>
          <div className="space-y-2">
            {opts.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <button onClick={() => setCorrect(i)}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-black active:scale-90"
                  style={{ background: correct === i ? C.win : C.cardHi, color: correct === i ? '#000' : C.text, border: `1px solid ${correct === i ? C.win : C.border}` }}>
                  {['A','B','C','D'][i]}
                </button>
                <input value={o} onChange={e => setOpts(arr => arr.map((x, idx) => idx === i ? e.target.value : x))} maxLength={80}
                  placeholder={correct === i ? 'Richtige Antwort' : `Antwort ${['A','B','C','D'][i]}`}
                  className="flex-1 rounded-lg px-3 py-2 text-sm"
                  style={{ background: C.cardHi, color: C.text, border: `1px solid ${correct === i ? C.win : C.border}` }} />
              </div>
            ))}
          </div>
        </Card>
        <div className="flex gap-2">
          <Btn color="ghost" size="md" onClick={onClose} className="flex-1">Abbrechen</Btn>
          <Btn color="multi" size="md" onClick={submit} disabled={!q.trim() || opts.some(o => !o.trim())} className="flex-1">Absenden</Btn>
        </div>
        {pool.length > 0 && me.role === 'leader' && (
          <Card className="p-3 mt-4">
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Pool ({pool.length})</div>
            <div className="space-y-1 max-h-48 overflow-auto">
              {pool.map(p => (
                <div key={p.id} className="flex items-start gap-2 text-xs py-1 border-b" style={{ borderColor: C.border }}>
                  <div className="flex-1">
                    <div className="font-bold">{p.q}</div>
                    <div style={{ color: C.muted }}>✓ {p.opts[p.correct]} · von {p.by}</div>
                  </div>
                  <button onClick={() => send({ type: 'delete-alex-question', id: p.id })}
                    className="w-6 h-6 rounded-md flex items-center justify-center" style={{ color: C.dim, background: C.cardHi }}>×</button>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </Modal>
  );
};

// ============================================================
//                  HISTORY CARD
// ============================================================
const HistoryCard = ({ history, me, onDelete }) => {
  if (!history || !history.length) return null;
  const isLeader = me?.role === 'leader';
  return (
    <Card className="p-4">
      <div className="text-[10px] uppercase tracking-widest mb-3" style={{ color: C.dim }}>
        Verlauf · {history.length} Spiele · {computeScores(history).alex + computeScores(history).challenger} Punkte vergeben
      </div>
      <div className="space-y-1.5">
        {[...history].map((h, i) => {
          const pts = h.points ?? (i + 1);
          return (
            <div key={h.ts || i} className="flex items-center gap-2 text-sm py-1" style={{ borderBottom: i < history.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <div className="w-7 text-center text-[10px] font-black opacity-50">#{i + 1}</div>
              <div className="flex-1 truncate">{h.name}</div>
              <div className="text-xs font-bold uppercase tracking-widest" style={{ color: h.winner === 'alex' ? C.alex : C.group }}>
                {h.winner === 'alex' ? 'Alex' : 'Gruppe'}
              </div>
              <div className="w-10 text-right font-mono font-bold" style={{ color: h.winner === 'alex' ? C.alex : C.group }}>+{pts}</div>
              {isLeader && (
                <button onClick={() => onDelete?.(h)}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-xs active:scale-90"
                  style={{ color: C.dim, background: C.cardHi }}
                  title="Eintrag löschen, Punkte neu rechnen">×</button>
              )}
            </div>
          );
        })}
      </div>
      {isLeader && history.length > 0 && (
        <div className="text-[10px] mt-3 text-center" style={{ color: C.dim }}>
          × löscht einen Eintrag · nachfolgende Spiele rücken auf und Punkte werden neu vergeben
        </div>
      )}
    </Card>
  );
};

// ============================================================
//                  PLAYER / ALEX VIEW
// ============================================================
const PlayerView = ({ shared, me, send, strokes, scores, onOpenAlexSubmit, onOpenBeer, onOpenChat, onOpenChallenge }) => {
  const activity = shared.activity;
  const playerAccepts = me.role === 'player';
  const [showSuggestForm, setShowSuggestForm] = useState(false);
  const myProposals = (shared.customGames || []).filter(g => g.byUserId === me.userId);
  const onSuggest = ({ name, short, icon, mode }) => {
    send({ type: 'suggest-game', name, short, icon, mode });
    setShowSuggestForm(false);
  };
  const canSuggest = me.role !== 'alex';
  const showVote     = activity?.type === 'audience-vote' && playerAccepts;
  const showEstimate = activity?.type === 'estimate';
  const showFlag     = activity?.type === 'flag-quiz' || activity?.type === 'general-quiz';
  const showAlexQuiz = activity?.type === 'alex-quiz-live';
  const showReaction = activity?.type === 'reaction-battle';
  const showWhoWould = activity?.type === 'who-would-rather';
  const showBuzzer   = activity?.type === 'buzzer';
  const showPict     = activity?.type === 'pictionary';
  const showAvgEst   = activity?.type === 'avg-estimate';
  const showWaiting  = !showVote && !showEstimate && !showFlag && !showAlexQuiz && !showReaction && !showWhoWould && !showBuzzer && !showPict && !showAvgEst;

  return (
    <div className="p-4 space-y-4 pb-24">
      {showVote     && <PlayerVotePrompt activity={activity} me={me} shared={shared} send={send} />}
      {showEstimate && <PlayerEstimatePrompt activity={activity} me={me} shared={shared} send={send} />}
      {showFlag     && <FlagQuizPlayer activity={activity} me={me} shared={shared} send={send} />}
      {showAlexQuiz && <AlexQuizLivePlayer activity={activity} me={me} shared={shared} send={send} />}
      {showReaction && <ReactionBattlePlayer activity={activity} me={me} shared={shared} send={send} />}
      {showWhoWould && <WhoWouldRatherPlayer activity={activity} me={me} shared={shared} send={send} />}
      {showBuzzer   && <BuzzerPlayer activity={activity} me={me} shared={shared} send={send} />}
      {showPict     && <PictionaryPlayer activity={activity} me={me} strokes={strokes} send={send} />}
      {showAvgEst   && <AvgEstimatePlayer activity={activity} me={me} shared={shared} send={send} />}
      {showWaiting && (
        <>
          <Scoreboard scores={scores} big />
          {activity?.type === 'audience-vote' && me.role === 'alex' && (
            <Card className="p-5 text-center" style={{ borderColor: C.group }}>
              <div className="text-3xl mb-2">👥</div>
              <Display className="text-xl block" style={{ color: C.group }}>Publikum stimmt ab</Display>
              <div className="text-xs mt-2" style={{ color: C.muted }}>Sie tippen gerade für dich. Du bist still ;)</div>
            </Card>
          )}
          {!activity && (
            <Card className="p-5 text-center">
              <div className="text-3xl mb-2">⏳</div>
              <Display className="text-xl block">Warten auf nächstes Spiel</Display>
              <div className="text-xs mt-2" style={{ color: C.muted }}>
                {me.role === 'alex' ? 'Der Spielleiter wählt gleich die nächste Disziplin.' : 'Wenn ein Multi-User-Spiel kommt, ploppt es hier auf.'}
              </div>
            </Card>
          )}
          <div className="grid grid-cols-4 gap-2">
            <Btn color="neutral" size="md" onClick={onOpenBeer}>🍺</Btn>
            <Btn color="neutral" size="md" onClick={onOpenChat}>💬</Btn>
            <Btn color="neutral" size="md" onClick={onOpenChallenge}>🎮</Btn>
            <Btn color="multi" size="md" onClick={onOpenAlexSubmit}>❓ Alex</Btn>
          </div>
          {canSuggest && (
            <Card className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest" style={{ color: C.dim }}>
                  Eigenes Spiel vorschlagen
                </div>
                {!showSuggestForm && (
                  <button onClick={() => setShowSuggestForm(true)}
                    className="text-xs font-bold px-3 py-1 rounded-lg active:scale-95 transition"
                    style={{ color: C.group, background: C.cardHi, border: `1px solid ${C.groupDim}` }}>
                    + Spiel vorschlagen
                  </button>
                )}
              </div>
              {showSuggestForm && <CustomGameForm onAdd={onSuggest} onCancel={() => setShowSuggestForm(false)} />}
              {myProposals.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: C.dim }}>
                    Meine Vorschläge · {myProposals.length}
                  </div>
                  {myProposals.map(g => (
                    <div key={g.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ background: C.cardHi }}>
                      <div className="text-lg">{g.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{g.name}</div>
                        <div className="text-[10px] truncate" style={{ color: C.muted }}>{g.short}</div>
                      </div>
                      <button onClick={() => {
                        if (confirm(`Vorschlag „${g.name}" zurückziehen?`)) send({ type: 'unsuggest-game', id: g.id });
                      }}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-lg active:scale-90"
                        style={{ color: C.dim, background: C.card }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
          <PoolCard shared={shared} scores={scores} />
          <HistoryCard history={shared.history} me={me} />
        </>
      )}
    </div>
  );
};

// ============================================================
//                  LEADER GAME DETAIL (live games)
// ============================================================
const GameDetail = ({ game, onBack, onWin, nextPoints }) => {
  const renderTool = () => {
    switch (game.tool) {
      case 'minute': return <MinuteTool />;
      case 'dice': return <DiceTool />;
      case 'letter': return <LetterTool />;
      case 'memorize': return <MemorizeTool />;
      case 'molkky': return <MolkkyTool />;
      case 'nim': return <NimTool />;
      case 'reaction': return <ReactionTool />;
      case 'counter': return <CounterTool />;
      case 'w4race': return <W4RaceTool />;
      default: return null;
    }
  };
  return (
    <div className="p-4 space-y-4 pb-24">
      <div className="flex items-start gap-3">
        <div className="text-5xl">{game.icon}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: C.group }}>{game.cat}</div>
            <ModeBadge mode={game.mode} />
          </div>
          <Display className="text-3xl leading-none">{game.name}</Display>
        </div>
      </div>
      <Card className="p-3 text-center" style={{ borderColor: C.group }}>
        <div className="text-[10px] uppercase tracking-widest" style={{ color: C.dim }}>Spiel #{nextPoints} · Punkte für Sieger</div>
        <Display className="text-3xl" style={{ color: C.group }}>{nextPoints}</Display>
      </Card>
      <Card className="p-4"><div className="text-sm leading-relaxed">{game.desc}</div></Card>
      <Card className="p-3 text-xs" style={{ color: C.muted }}>
        <span className="font-bold uppercase tracking-widest text-[10px]" style={{ color: C.dim }}>Material: </span>{game.mat}
      </Card>
      {renderTool() && <div className="pt-2">{renderTool()}</div>}
      <div className="pt-4">
        <div className="text-[10px] uppercase tracking-widest mb-2 text-center" style={{ color: C.dim }}>Wer hat gewonnen?</div>
        <div className="grid grid-cols-2 gap-3">
          <Btn color="alex" size="xl" onClick={() => { onWin('alex', game); onBack(); }}>
            <Display className="text-2xl">ALEX</Display>
            <div className="text-xs opacity-80 mt-1">+{nextPoints} Punkte</div>
          </Btn>
          <Btn color="group" size="xl" onClick={() => { onWin('challenger', game); onBack(); }}>
            <Display className="text-2xl">GRUPPE</Display>
            <div className="text-xs opacity-80 mt-1">+{nextPoints} Punkte</div>
          </Btn>
        </div>
      </div>
    </div>
  );
};

// ============================================================
//                  CUSTOM GAME FORM
// ============================================================
const CustomGameForm = ({ onAdd, onCancel }) => {
  const [name, setName] = useState('');
  const [short, setShort] = useState('');
  const [icon, setIcon] = useState('🎲');
  const [mode, setMode] = useState('live');
  const icons = ['🎲', '🍺', '⚽', '🎯', '🥨', '🎨', '🃏', '🎬', '🥁', '🧩', '🎭', '🏆', '🪙', '🍻', '🎺'];
  return (
    <Card className="p-4 space-y-3" style={{ borderColor: C.group }}>
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.group }}>Eigenes Spiel hinzufügen</div>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Spielname"
        className="w-full rounded-lg px-3 py-3 text-base outline-none"
        style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} autoFocus />
      <input value={short} onChange={e => setShort(e.target.value)} placeholder={'Kurzregel (z.B. „Weitester Wurf gewinnt")'}
        className="w-full rounded-lg px-3 py-3 text-sm outline-none"
        style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} />
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => setMode('live')}
          className="rounded-lg px-3 py-2 text-xs font-bold flex items-center justify-center gap-1"
          style={{ background: mode === 'live' ? C.cardHi : C.card, color: mode === 'live' ? C.live : C.muted, border: `1px solid ${mode === 'live' ? C.live : C.border}` }}>
          🌍 Live · vor Ort
        </button>
        <button onClick={() => setMode('multi')}
          className="rounded-lg px-3 py-2 text-xs font-bold flex items-center justify-center gap-1"
          style={{ background: mode === 'multi' ? C.cardHi : C.card, color: mode === 'multi' ? C.multi : C.muted, border: `1px solid ${mode === 'multi' ? C.multi : C.border}` }}>
          📱 Multi · Smartphone
        </button>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: C.dim }}>Icon</div>
        <div className="flex flex-wrap gap-1">
          {icons.map(i => (
            <button key={i} onClick={() => setIcon(i)}
              className="w-10 h-10 rounded-lg flex items-center justify-center text-2xl transition active:scale-90"
              style={{ background: icon === i ? C.cardHi : 'transparent', border: `1px solid ${icon === i ? C.group : C.border}` }}>{i}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Btn color="ghost" size="md" onClick={onCancel} className="flex-1">Abbrechen</Btn>
        <Btn color="group" size="md" onClick={() => name.trim() && onAdd({ name: name.trim(), short: short.trim(), icon, mode })} disabled={!name.trim()} className="flex-1">
          Hinzufügen
        </Btn>
      </div>
    </Card>
  );
};

// ============================================================
//                  LEADER HOME
// ============================================================
const LeaderHome = ({ shared, scores, me, onGame, onEstimate, onReset, onRoll, onOpenAlexSubmit, onOpenBeer, onDeleteHistory, updateShared }) => {
  const [showForm, setShowForm] = useState(false);
  const customGames = shared.customGames || [];
  const allGames = [...games, ...customGames];
  const playedIds = new Set((shared.history || []).map(h => h.gameId));
  const totalGames = (shared.history || []).length;
  const nextPoints = totalGames + 1;
  const winner = scores.alex > scores.challenger ? 'alex' : scores.challenger > scores.alex ? 'grp' : null;
  const alexQuizPoolCount = shared.alexQuiz?.pool?.length || 0;

  const addCustom = ({ name, short, icon, mode }) => {
    const newGame = {
      id: `c-${Date.now()}`, name,
      short: short || 'Spontanes Spiel',
      desc: short || 'Vor Ort erfundenes Spiel. Regeln macht ihr selbst.',
      cat: 'Spontan', icon: icon || '🎲', mat: 'Improvisation', tool: null, custom: true, mode: mode || 'live',
    };
    updateShared(s => ({ ...s, customGames: [...(s.customGames || []), newGame] }));
    setShowForm(false);
  };
  const removeCustom = (id) => {
    if (!confirm('Dieses spontane Spiel entfernen?')) return;
    updateShared(s => ({ ...s, customGames: (s.customGames || []).filter(g => g.id !== id) }));
  };
  return (
    <div className="p-4 space-y-4 pb-24">
      <Scoreboard scores={scores} big />
      <Card className="p-3 text-center" style={{ borderColor: C.group }}>
        <div className="text-[10px] uppercase tracking-widest" style={{ color: C.dim }}>Nächstes Spiel</div>
        <Display className="text-2xl" style={{ color: C.group }}>#{nextPoints} · {nextPoints} Punkte</Display>
      </Card>
      <div className="grid grid-cols-3 gap-2">
        <Btn color="neutral" size="md" onClick={onEstimate}>📏 Schätzfrage</Btn>
        <Btn color="neutral" size="md" onClick={onRoll}>🎲 TN würfeln</Btn>
        <Btn color="neutral" size="md" onClick={onOpenBeer}>🍺 Bier</Btn>
      </div>
      <div className="pt-2">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: C.dim }}>
            Disziplinen · {allGames.length}
          </div>
          {!showForm && (
            <button onClick={() => setShowForm(true)} className="text-xs font-bold px-3 py-1 rounded-lg active:scale-95 transition"
              style={{ color: C.group, background: C.cardHi, border: `1px solid ${C.groupDim}` }}>+ Eigenes Spiel</button>
          )}
        </div>
        {showForm && <div className="mb-3"><CustomGameForm onAdd={addCustom} onCancel={() => setShowForm(false)} /></div>}
        <div className="space-y-2">
          {allGames.map(g => {
            const played = playedIds.has(g.id);
            const showAlexBadge = g.tool === 'alex-quiz-live';
            return (
              <Card key={g.id} onClick={() => onGame(g)} style={{ opacity: played ? 0.55 : 1, borderColor: g.custom ? C.groupDim : C.border }} className="p-3">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">{g.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Display className="text-lg truncate">{g.name}</Display>
                      <ModeBadge mode={g.mode || 'live'} />
                      {showAlexBadge && (
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
                          style={{ background: C.cardHi, color: alexQuizPoolCount > 0 ? C.multi : C.dim }}>
                          {alexQuizPoolCount} Frage{alexQuizPoolCount === 1 ? '' : 'n'}
                        </span>
                      )}
                      {g.custom && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded" style={{ background: C.cardHi, color: C.group }}>SPONTAN</span>}
                      {played && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded" style={{ background: C.cardHi, color: C.dim }}>✓</span>}
                    </div>
                    <div className="text-xs truncate" style={{ color: C.muted }}>{g.short}</div>
                    {g.by && <div className="text-[10px] truncate mt-0.5" style={{ color: C.group }}>↑ von {g.by}</div>}
                  </div>
                  {g.custom ? (
                    <button onClick={(e) => { e.stopPropagation(); removeCustom(g.id); }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-lg active:scale-90"
                      style={{ color: C.dim, background: C.cardHi }}>×</button>
                  ) : (
                    <div className="text-xs" style={{ color: C.dim }}>›</div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
      <Btn color="ghost" size="md" onClick={onOpenAlexSubmit} className="w-full">❓ Alex-Frage einreichen / Pool verwalten</Btn>
      <HistoryCard history={shared.history} me={me} onDelete={onDeleteHistory} />
      {totalGames > 0 && (
        <div className="pt-4">
          <button onClick={onReset} className="w-full py-3 text-xs uppercase tracking-widest" style={{ color: C.dim }}>Tag zurücksetzen</button>
        </div>
      )}
    </div>
  );
};

// ============================================================
//                  NAME PICKER (Player only)
// ============================================================
const NamePicker = ({ onSubmit }) => {
  const [name, setName] = useState('');
  return (
    <div className="p-6 pt-16 space-y-8">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.4em] mb-3" style={{ color: C.group }}>Bierwandertag · Spalt</div>
        <Display className="text-6xl block leading-none" style={{ color: C.text }}>SCHLAG</Display>
        <Display className="text-3xl block leading-none mt-1" style={{ color: C.muted }}>DEN</Display>
        <Display className="text-6xl block leading-none mt-1" style={{ color: C.alex }}>ALEX</Display>
      </div>
      <Card className="p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-widest" style={{ color: C.dim }}>Wie heißt du?</div>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Vorname"
          className="w-full rounded-lg px-3 py-3 text-lg font-bold outline-none"
          style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}` }} autoFocus
          onKeyDown={e => { if (e.key === 'Enter' && name.trim()) onSubmit(name.trim()); }} />
      </Card>
      <Btn color="group" size="lg" disabled={!name.trim()} onClick={() => onSubmit(name.trim())} className="w-full">
        Los geht's
      </Btn>
    </div>
  );
};

const FullScreen = ({ title, sub, color = C.muted }) => (
  <div className="min-h-screen flex items-center justify-center p-6" style={{ background: C.bg, color: C.text }}>
    <div className="text-center max-w-sm">
      <Display className="text-4xl block mb-3" style={{ color }}>{title}</Display>
      {sub && <div className="text-sm" style={{ color: C.muted }}>{sub}</div>}
    </div>
  </div>
);

// ============================================================
//                         APP ROOT
// ============================================================
const NAME_KEY = 'sda-name-v1';

export default function App() {
  const { shared, me, status, presence, strokes, send, updateShared, setName } = useConnection();
  const [view, setView] = useState('home');
  const [game, setGame] = useState(null);
  const [askName, setAskName] = useState(false);
  const [showOnline, setShowOnline] = useState(false);
  const [showAlexSubmit, setShowAlexSubmit] = useState(false);
  const [showBeer, setShowBeer] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [lastSeenChatTs, setLastSeenChatTs] = useState(0);

  // Player initial name handling
  useEffect(() => {
    if (!me) return;
    if (me.role !== 'player') { setAskName(false); return; }
    const stored = localStorage.getItem(NAME_KEY);
    if (stored) {
      if (me.name !== stored) setName(stored);
      setAskName(false);
    } else {
      setAskName(true);
    }
  }, [me?.role]);

  // Mark chat as read when modal opens
  useEffect(() => {
    if (showChat) setLastSeenChatTs(Date.now());
  }, [showChat]);

  const scores = useMemo(() => computeScores(shared.history), [shared.history]);
  const onlineCount = presence.length;
  const chatUnread = useMemo(() => {
    if (!me) return 0;
    return (shared.chats || []).filter(m => m.from !== me.userId && m.ts > lastSeenChatTs && (m.to === 'all' || m.to === me.userId)).length;
  }, [shared.chats, me?.userId, lastSeenChatTs]);

  // Auto-resume a running multi-activity for leader. Must be called every render — no conditional early-return above this hook.
  const a = shared.activity;
  const aType = a?.type;
  const isMultiActivity = !!(aType && ['flag-quiz','general-quiz','alex-quiz-live','reaction-battle','who-would-rather','buzzer','pictionary','avg-estimate'].includes(aType));
  useEffect(() => {
    if (me?.role === 'leader' && isMultiActivity && view === 'home') {
      setView('multi-active');
    }
  }, [me?.role, isMultiActivity, view]);

  if (status === 'no-token') return <FullScreen title="Kein Token" sub="Diese Seite braucht einen Zugangscode in der URL (?t=…). Frag den Spielleiter." color={C.alex} />;
  if (status === 'denied') return <FullScreen title="Token ungültig" sub="Der Zugangscode wurde nicht erkannt. Frag den Spielleiter nach einem aktuellen Link." color={C.alex} />;
  if (!me) return <FullScreen title="Verbinde…" />;

  if (askName) {
    return (
      <div style={{ background: C.bg, color: C.text, minHeight: '100vh' }}>
        <StatusBanner status={status} />
        <NamePicker onSubmit={n => { localStorage.setItem(NAME_KEY, n); setName(n); setAskName(false); }} />
      </div>
    );
  }

  const onWin = (who, g) => {
    updateShared(s => {
      const points = (s.history || []).length + 1;
      const newHistory = [...(s.history || []), { gameId: g.id, name: g.name, winner: who, points, ts: Date.now() }];
      return { ...s, history: newHistory, activity: null };
    });
  };
  const onMultiWin = (who) => {
    const g = game || (games.find(x => x.id === shared.activity?.gameId));
    if (!g) return;
    onWin(who, g);
    setView('home'); setGame(null);
  };
  const onReset = () => {
    if (!confirm('Wirklich alle Punkte und Historie für ALLE zurücksetzen? (Eigene Spiele bleiben)')) return;
    updateShared(s => ({ ...s, history: [], activity: null }));
  };
  const onDeleteHistory = (entry) => {
    if (!confirm(`„${entry.name}" wirklich aus der History löschen? Punkte werden neu vergeben.`)) return;
    updateShared(s => ({
      ...s,
      history: (s.history || []).filter(h => h.ts !== entry.ts).map((h, i) => ({ ...h, points: i + 1 })),
    }));
  };
  const goHome = () => { setView('home'); setGame(null); };
  const goGame = (g) => {
    setGame(g);
    if (g.mode === 'multi') {
      if (g.tool === 'flag-quiz') {
        const questions = buildFlagSet(10);
        const id = 'fq-' + Date.now();
        updateShared(s => ({ ...s, activity: { type: 'flag-quiz', id, gameId: g.id, gameName: g.name, questions, current: 0, phase: 'playing', ts: Date.now() } }));
        setView('multi-active');
      } else if (g.tool === 'general-quiz') {
        // Server picks 10 unused questions from its 500-question pool
        const id = 'gq-' + Date.now();
        updateShared(s => ({ ...s, activity: { type: 'general-quiz', id, gameId: g.id, gameName: g.name, current: 0, phase: 'playing', ts: Date.now() } }));
        setView('multi-active');
      } else if (g.tool === 'alex-quiz-live') {
        const pool = shared.alexQuiz?.pool || [];
        if (pool.length < 1) { alert('Im Alex-Quiz-Pool ist noch keine Frage. Bitte erst Fragen einreichen.'); return; }
        const picked = [...pool].sort(() => Math.random() - 0.5).slice(0, Math.min(5, pool.length));
        const id = 'aq-' + Date.now();
        updateShared(s => ({ ...s, activity: { type: 'alex-quiz-live', id, gameId: g.id, gameName: g.name, questions: picked, current: 0, phase: 'playing', ts: Date.now() } }));
        setView('multi-active');
      } else if (g.tool === 'reaction-battle') {
        const id = 'rb-' + Date.now();
        updateShared(s => ({ ...s, activity: { type: 'reaction-battle', id, gameId: g.id, gameName: g.name, phase: 'wait', ts: Date.now() } }));
        setView('multi-active');
      } else if (g.tool === 'who-would-rather') {
        setView('multi-setup');
      } else if (g.tool === 'buzzer') {
        const id = 'bz-' + Date.now();
        updateShared(s => ({ ...s, activity: { type: 'buzzer', id, gameId: g.id, gameName: g.name, question: '', ts: Date.now() } }));
        setView('multi-active');
      } else if (g.tool === 'pictionary') {
        const id = 'pi-' + Date.now();
        // Server will pick drawer + word in side-effect on state-replace
        updateShared(s => ({ ...s, activity: { type: 'pictionary', id, gameId: g.id, gameName: g.name, rounds: [], ts: Date.now() } }));
        setView('multi-active');
      } else if (g.tool === 'avg-estimate') {
        const id = 'ae-' + Date.now();
        updateShared(s => ({ ...s, activity: { type: 'avg-estimate', id, gameId: g.id, gameName: g.name, prompt: '', actual: null, rounds: [], phase: 'asking', ts: Date.now() } }));
        setView('multi-active');
      }
    } else {
      setView(g.tool === 'quiz' ? 'quiz' : 'game');
    }
  };
  // Opens the presence panel where leader picks online/all + sees TN list
  const onRoll = () => setShowOnline(true);
  const dismissRoulette = () => updateShared(s => ({ ...s, activity: null }));
  const onOpenWhiteboard = () => {
    if (shared.activity?.type === 'pictionary') return;
    if (shared.drawing?.mode !== 'whiteboard') {
      send({ type: 'whiteboard-open' });
    }
    setShowWhiteboard(true);
  };

  // a/aType/isMultiActivity already computed above the early returns

  return (
    <div style={{ background: C.bg, color: C.text, minHeight: '100vh' }}>
      <StatusBanner status={status} />
      <Header scores={scores} me={me} onHome={goHome} view={view} onlineCount={onlineCount}
        onShowOnline={() => setShowOnline(true)}
        onChat={() => setShowChat(true)}
        chatUnread={chatUnread}
        onChallenge={() => setShowChallenge(true)}
        onWhiteboard={onOpenWhiteboard}
        whiteboardActive={shared.drawing?.mode === 'whiteboard'} />

      {/* Name roulette overlay (across all roles) */}
      {aType === 'name-roulette' && (
        <NameRouletteOverlay activity={a} me={me} isLeader={me.role === 'leader'} onDismiss={dismissRoulette} />
      )}

      {(me.role === 'player' || me.role === 'alex') && (
        <PlayerView shared={shared} me={me} send={send} strokes={strokes} scores={scores}
          onOpenAlexSubmit={() => setShowAlexSubmit(true)}
          onOpenBeer={() => setShowBeer(true)}
          onOpenChat={() => setShowChat(true)}
          onOpenChallenge={() => setShowChallenge(true)} />
      )}
      {me.role === 'leader' && (
        <>
          {view === 'home' && !isMultiActivity && (
            <LeaderHome shared={shared} scores={scores} me={me} onGame={goGame} onEstimate={() => setView('estimate')}
              onReset={onReset} onRoll={onRoll} onDeleteHistory={onDeleteHistory}
              onOpenAlexSubmit={() => setShowAlexSubmit(true)}
              onOpenBeer={() => setShowBeer(true)}
              updateShared={updateShared} />
          )}
          {view === 'game' && game && <GameDetail game={game} onBack={goHome} onWin={onWin} nextPoints={(shared.history || []).length + 1} />}
          {view === 'quiz' && game && (
            <div className="p-4 pb-24">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-5xl">👑</div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: C.group }}>Quiz-Finale</div>
                  <Display className="text-3xl">Wer wird Alex-ionär</Display>
                </div>
              </div>
              <QuizMode shared={shared} updateShared={updateShared} onComplete={(who) => { onWin(who, game); setView('home'); setGame(null); }} />
            </div>
          )}
          {view === 'estimate' && (
            <div className="p-4 pb-24">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-5xl">📏</div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: C.group }}>Bonusrunde</div>
                  <Display className="text-3xl">Schätzfrage</Display>
                </div>
              </div>
              <EstimateMode shared={shared} updateShared={updateShared} />
            </div>
          )}
          {view === 'multi-setup' && game?.tool === 'who-would-rather' && (
            <WhoWouldRatherSetup presence={presence}
              onStart={(question, b) => {
                const id = 'ww-' + Date.now();
                updateShared(s => ({ ...s, activity: { type: 'who-would-rather', id, gameId: game.id, gameName: game.name, question, options: ['Alex', b], ts: Date.now() } }));
                setView('multi-active');
              }}
              onCancel={() => { setView('home'); setGame(null); }} />
          )}
          {view === 'multi-active' && isMultiActivity && (
            <>
              {(aType === 'flag-quiz' || aType === 'general-quiz') && (
                <FlagQuizLeader activity={a} shared={shared} updateShared={updateShared} onComplete={onMultiWin} />
              )}
              {aType === 'alex-quiz-live' && (
                <AlexQuizLiveLeader activity={a} shared={shared} updateShared={updateShared} onComplete={onMultiWin} />
              )}
              {aType === 'reaction-battle' && (
                <ReactionBattleLeader activity={a} shared={shared} updateShared={updateShared} onComplete={onMultiWin} />
              )}
              {aType === 'who-would-rather' && (
                <WhoWouldRatherLeader activity={a} shared={shared} presence={presence} updateShared={updateShared} onComplete={onMultiWin} />
              )}
              {aType === 'buzzer' && (
                <BuzzerLeader activity={a} shared={shared} updateShared={updateShared} onComplete={onMultiWin} />
              )}
              {aType === 'pictionary' && (
                <PictionaryLeader activity={a} shared={shared} strokes={strokes} send={send} updateShared={updateShared} onComplete={onMultiWin} />
              )}
              {aType === 'avg-estimate' && (
                <AvgEstimateLeader activity={a} shared={shared} updateShared={updateShared} onComplete={onMultiWin} />
              )}
            </>
          )}
        </>
      )}

      <PresencePanel open={showOnline} onClose={() => setShowOnline(false)} presence={presence} me={me}
        manualParticipants={shared.manualParticipants || []} send={send} />
      <AlexQuestionSubmit open={showAlexSubmit} onClose={() => setShowAlexSubmit(false)}
        send={send} pool={shared.alexQuiz?.pool || []} me={me} />
      <BeerModal open={showBeer} onClose={() => setShowBeer(false)} />
      <ChatModal open={showChat} onClose={() => setShowChat(false)} chats={shared.chats} presence={presence} me={me} send={send} />
      <ChallengeModal open={showChallenge} onClose={() => setShowChallenge(false)} presence={presence} me={me} send={send} />
      <WhiteboardMini open={showWhiteboard} onClose={() => setShowWhiteboard(false)}
        strokes={strokes} send={send} me={me} drawing={shared.drawing} />

      <ChallengeInbox challenges={shared.challenges || []} me={me} send={send} />
      <MiniGamesPanel miniGames={shared.miniGames || {}} me={me} send={send} />

      <div className="text-center text-[9px] pb-4 pt-2 uppercase tracking-[0.25em]" style={{ color: C.dim }}>
        Schlag den Alex · Bierwandertag Spalt · Multi-User
      </div>
    </div>
  );
}
